/* 189 §C — `import React`: react/react-in-jsx-scope is counted per JSX
   element and this is the rule's own remedy (168A's method). */
import React from "react";
import { findHeaderCol as findC } from "../lib/xlsxHeaders"; // A15, brief 65
import { tempId } from "../lib/tempId"; // 207 §1.2 — a temporary id is never a server id
import { matterCast } from "../lib/matterCast"; // 119 — §1c's pre-step
// 141 §2.2 — the per-matter client status: the 136 §1 pill, the shared colours
// and option rows the CCV roster wears, and the route that is its ONLY writer.
import StatusPill from "../components/StatusPill";
import { linkStatusCfg, linkStatusOptions } from "../lib/linkStatus";
import { setCaseClientStatus, setCaseCounselStatus, setCaseContactStatus } from "../api"; // 214 §3 — the two link states
import { counselChipExtra, clientCounselChipExtra } from "../components/CounselChipExtra"; // 214 §2–§4
import LinkSuspendDialog from "../components/LinkSuspendDialog"; // 214 §3 — « Suspendre » asks the date
import { actingFirst, counselIdsOf, isClientCounsel, isSuspended, linkOf, type CounselLink, type LinkMap, type LinkState, type SetRowsFn } from "../lib/counselLinks"; // 214
import { refusedReasons, sheetRowMatch, withoutAbsentColumns, type RefusedRow } from "../lib/sheetColumns"; // 214 addendum 3 §24 — a column the sheet lacks leaves the stored value; addendum 5 §29 — where a row lands
import ImportLossDialog from "../components/ImportLossDialog"; // 214 addendum 4 §25 [RULED 63] — an import asks before it loses anything
import { addLoss, addRowsTaken, hasEmptied, hasLoss, listPools, matterLabel, matterTitle, newLossPlan, poolNamer, rowLosses, withoutEmptying, type DiffField, type LossPlan } from "../lib/importLoss"; // 214 addendum 4 §25
import type { ToastFn } from "../components/Toast"; // 214 — `toast` typed once at the seam
import type { TeBulkUpdateResult } from "../api"; // 145 §1 — the bulk-edit result the table hands back
// 118 §6 (the flatten) + 119 §5 (the three groups, one grouping function)
import {
  meetingRoster,
  teamIdSet,
  PEOPLE_GROUP_KEY,
} from "../lib/meetingAttendees";
import { useState, useEffect, useRef, Fragment, type CSSProperties } from "react";
import {
  MessageCircle,
  Phone,
  AlertTriangle,
  Gavel,
  Info,
  Pencil,
  Trash2,
  Play,
  X,
  Clock,
  MapPin,
  Send,
  Archive,
  FileText,
  Share2,
  Mail,
  Calendar,
  Users,
  Download,
  Upload,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Pin,
  PinOff,
  CalendarClock,
  HardDrive,
  Cloud,
  Folder,
  Globe,
  Link2,
  Database,
  ExternalLink as ExtLink2,
  BookOpen,
  Paperclip,
  Server,
  Search,
  Lock,
} from "lucide-react";
import SubtaskChecklist from "../components/SubtaskChecklist";
import SubtaskCounter, { EventSubtasks } from "../components/SubtaskCounter";
import {
  I,
  SK,
  sv,
  DOC_STATUSES,
  ctc,
  fc,
  fq,
  fd,
  faddr,
  pname,
  nid,
  pm,
  TASK_STATUSES,
  /* Round 15 (C90 hygiene) — STAGE_TYPES / REMINDER_OPTS were referenced as
     empty-prop fallbacks below but never imported: a render with an empty
     stageTypesList/reminderOptsList would have thrown the same ReferenceError
     class that blanked the collective-cases list. 05n removed the STAGE_TYPES
     half — the fallback it guarded was unreachable, because useTaxonomyList
     answers KIND_TO_DEFAULT and never an empty list for that kind. The
     REMINDER_OPTS half stands, unchanged. */
  REMINDER_OPTS,
  itemLevelMap,
  itemLevelOrder,
  itemLevelBase,
  contrastText,
  scrollToEv,
  queueScrollToEv,
  checkScrollToEv,
  peekScrollToEv,
  getLatestRegNum,
  getLatestRegStage,
  checkScrollToTk,
  eisenQuadrant,
  firmNameSlug,
} from "../utils";
import {
  LockButton,
  MyAllToggle,
  InitialsBadge,
  AddButton,
  SectionHeader,
  ModalLayout,
} from "../components/SharedUI";
import { ArchivedHoleBadge } from "../components/SharedUI";
import CallAvatar from "../components/CallAvatar";
import { EisenBadge, EISEN_LABELS, eisenLabel } from "../EisenBadge";
import { TaskMatrix } from "../TaskMatrix";
import ProFormaModal, {
  getInvoiceHeaderLabel,
} from "../components/modals/ProFormaModal";
import InvoiceTable from "../components/InvoiceTable";
import TimeEntryTable from "../components/TimeEntryTable";
import { Grid2x2 } from "lucide-react";
import { loadXlsx } from "../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is read
import EntityList from "../components/EntityList";
import { buildMatterListCfg, columnGateOf } from "../lib/matterListCfg";
import type { ProspectFilterValue } from "../lib/matterListCfg"; // 139 §2.3
import { useTranslation } from "../i18n";
import { importedCaseLanguage } from "../lib/caseLanguage"; // 209 §19 — an import's language column
import { useRoleLabels } from "../hooks/useRoleLabels";
import { computeCaseFinancials, getOverheadsShare } from "../api";
// Brief 87 §1c — the Financial summary's payment can now name the pro forma it
// settles, and that post is the stage-2 → stage-3 transition.
import { redMatterFin, redEntryFin, redCoFin, redRoleRows,
  redFinInv, redRecapInv,
} from "../lib/financialsClient";
import CaseEmailsSection from "../components/CaseEmailsSection";
import CaseFinancialPanel from "../components/CaseFinancialPanel";
import {
  useConfirmAndUndo,
  ConfirmDeleteDialog,
  UndoToast,
  useRefLatest,
  UNDO_WINDOW_MS, // 209 §25 — the party removal's bar runs the undo's own window
} from "../components/useConfirmAndUndo";
import { usePermissions } from "../hooks/usePermissions";
import { useTaxonomy, useTaxonomyList } from "../hooks/useTaxonomy";
// 190 §D — the board, a view over the same cases; a move writes a Bento placement.
import CaseBoardView from "./CaseBoardView";
import { BentoPicker } from "../components/BentoBoard"; // 190 [I] — the picker beside the list icon
import type { BoardCase, BoardField } from "./CaseBoardView";
/* [C] — what the pane reads off one of the list's column declarations
   (matterListCfg.tsx `colsT`, customFieldColumns.tsx): the key, the header
   label or its key, and the cell renderer EntityList calls the same way. */
type BoardColumn = { k: string; l?: string; lk?: string; raw?: boolean; r: (c: unknown, ctx: unknown) => React.ReactNode };
import { useBento } from "../hooks/useBento";
import type { Dispatch } from "react";
import type { TFn } from "../lib/taxonomyTypes";
import { customFieldColumns } from "../lib/customFieldColumns"; // 189 §B
import { firstMeetingTypeCode } from "../utils/taxonomyMap"; // 123 §3
import { clientsFamilyKey } from "../lib/matterLabel"; // 123 §4
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import { isDividerCode } from "../utils/taxonomyMap"; // 110 §3a
import TaxonomyOrphanMark from "../components/TaxonomyOrphanMark";
import CddCaseSection from "./CddCaseSection";
import CaseCalculatorSection from "./CaseCalculatorSection";
import { GenerateDocButton } from "../components/GenerateDocModal";
import CaseDocumentsSection from "../components/CaseDocumentsSection";
import CaseTimelineSection from "../components/CaseTimelineSection";
// 182 A.3 — the Notes section (the contact timeline), mounted after the timeline.
import InteractionSection from "../components/InteractionSection";
import CaseTaskRow from "../components/CaseTaskRow";
import CaseEventRow from "../components/CaseEventRow";
import { api } from "../api";
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad"; // 211 §10 — the timeline's own list, when it could not load
import { usePageList, usePageListContext, readList } from "../hooks/usePageList";
import { CT, RAD, FS } from "../lib/tokens";
import { TH, MC, ML, HC, TC, SH, EC, pctCo, ratCo, pct1 } from "../lib/moneyTableCells"; // A1, brief 65 (HC: 86 §2b); TC · SH · the two faces: 199 §F; EC · pct1: 214 addendum 1
import { TYPE_CHIP } from "../components/TypeTagBox"; // 205 §1 — the activity chip, the one definition
import { foreignOf } from "../components/MoneyStack"; // 169 R7
import type { Translate } from "../lib/interestRefusal"; // 199 §F — the translator's one shape, narrowed at the recap's seam

// BUG-A SWEEP — the CASE list importer, hoisted to module scope. It was
// `const CaII = () => {…}` inside CaView's render body, mounted as <CaII />,
// so every CaseView render gave React a new type and remounted the modal,
// destroying the parsed rows and the chosen import mode mid-review.
// Second of the four importers that carried this defect.
import { makeImportVocabularyReport } from "../lib/importVocabulary.mjs";
import { teamWithRpFirst, RP_BADGE_STYLE } from "../lib/teamOrder"; // brief 48 §2
import { readMatterMetrics } from "../lib/matterMetrics"; // A4, brief 65
import { mailDialogSeed } from "../lib/mailDialogSeed"; // A8, brief 65
import { nextEventDate } from "../lib/nextEventDate"; // A16, brief 65
import { sortStagesByStart } from "../lib/stageOrder"; // A18, brief 65
import { localTodayStr } from "../lib/localToday"; // A20, brief 65
import { makeImportCell } from "../lib/importCell"; // A10, brief 65
import { ClientContactsSublist, OpponentContactsSublist, OpponentCounselSublist } from "../components/GroupedContactsSublist"; // B24, brief 84; 214 §2 the counsel by party

/* 214 §3 — the one place the two link maps are merged back into a case row (the route answers the whole shaped
   case; this view has no form buffer, so only `ocuLinks` / `cpLinks` move — 141 §2.2's rule for `clLinks`).
   Module scope, so the setter's updater is two functions deep at most. */
interface LinkMapsRow {
  id?: number;
  ocuLinks: unknown;
  cpLinks: unknown;
}
const isRowWithId = (c: unknown, id: number): c is { id: number } => typeof c === "object" && c !== null && (c as { id?: unknown }).id === id;
const withLinkMaps = (row: LinkMapsRow) => (prev: unknown) => {
  const rid = row.id;
  if (!Array.isArray(prev) || !rid) return prev;
  return prev.map((c: unknown) => (isRowWithId(c, rid) ? { ...c, ocuLinks: row.ocuLinks ?? {}, cpLinks: row.cpLinks ?? {} } : c));
};
import { ImportErrorModal } from "../components/ImportErrorModal"; // B48, brief 84
import { ImportLoadingModal } from "../components/ImportLoadingModal"; // B60, brief 84
import { ImportTriggerButton } from "../components/ImportTriggerButton"; // B44, brief 84
import CaseTimeEntriesSection from "../components/CaseTimeEntriesSection"; // 86 Phase 2 step 1
import CaseEmailsPane from "../components/CaseEmailsPane"; // 86 Phase 2 step 2
import CaseInfoSection from "../components/CaseInfoSection"; // 86 Phase 2 step 3
import CaseTasksSection from "../components/CaseTasksSection"; // 86 Phase 2 step 4
import CaseStageTimeline, { stageMatchesQuery, type Stage as StageRow } from "../components/CaseStageTimeline"; // 86 Phase 2 step 5 · 186 (e)
import SectionSearch from "../components/SectionSearch"; // 186 (e) [RULED 186 (16)]
// 186 close (6) — the Stages block pages through the shared pager (B.7a's mechanism).
import { usePager, pageOf } from "../hooks/usePager";
import Pager from "../components/Pager";
import CaseAgendaSection from "../components/CaseAgendaSection"; // 86 Phase 2 step 6
import CaseTeamSection from "../components/CaseTeamSection"; // 86 Phase 2 step 7
import CaseDescriptionCard from "../components/CaseDescriptionCard"; // 86 Phase 2 step 10
import { SEC_COL_DEFAULTS } from "../lib/secColDefaults"; // C80, brief 84
import Table from "../components/Table";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

// AUTHORITY (155 §4): none at runtime — CaseImportModal and
// CollectiveCaseImportModal (CollectiveCaseView.tsx) are 92%-similar siblings
// of the four-importer family (the #209/Bug-A-sweep set); they differ
// deliberately by entity + save payload. A parse/preview/mapping fix lands in
// BOTH (and check JII/CII, the other two siblings).
function CaseImportModal({ caImportFile, setCaImportFile, ca, sCa, s, t, toast, pools }: any) {
// 05n §4 — see src/lib/importVocabulary.mjs.
const _vCaseType = useTaxonomy("case_type").entries;
const _vCaseStatus = useTaxonomy("case_status").entries;
// 209 §19 — the languages the dictionary carries: the case form's choices
const { available } = useTranslation() as { available: string[] };
const [rows, setRows] = useState<any[]>([]);
const [hdrs, setHdrs] = useState<any[]>([]);
const [parsed, setParsed] = useState(false);
const [error, setError] = useState<any>(null);
const [mode, setMode] = useState("add");
// 214 addendum 4 §25 [RULED 63] — the window's plan and the run it waits for (« Tout importer » / « Importer sans vider »)
const [lossAsk, setLossAsk] = useState<{ plan: LossPlan; run: Dispatch<boolean> } | null>(null);
useState(() => {
  const reader = new FileReader();
  reader.onload = async (ev) => {
    try {
      const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
      const wb = XLSX.read((ev.target as any).result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!json.length) {
        setError("Empty file");
        return;
      }
      setHdrs(Object.keys(json[0] as any));
      setRows(json);
      setParsed(true);
    } catch (err: any) {
      setError("Failed: " + err.message);
    }
  };
  reader.readAsArrayBuffer(caImportFile);
});
const doImport = () => {
  const h = hdrs;
  const refIdx = findC(h, "Ref", "ref");
  const idIdx = findC(h, "Id", "id"); // 214 addendum 5 §29 [RULED 65] — the matter's Id, the export's first column
  const ttIdx = findC(h, "Title", "title");
  const stIdx = findC(h, "Status", "status");
  const tpIdx = findC(h, "Type", "type", "ctype");
  const odIdx = findC(h, "OpenDate", "openDate");
  const deIdx = findC(h, "Description", "desc");
  const juIdx = findC(h, "JurisdictionId", "juId");
  // 214 addendum 3 §21 [ruling 62 (b)] — every partner: the plural header the export writes now, the singular one an
  // older sheet carries (one id), parsed as the other id cells are (the cell held the first partner alone).
  const rpIdx = findC(
    h,
    "ResponsiblePartnerIds",
    "ResponsiblePartnerId",
    "responsiblePartners",
    "responsiblePartner",
  );
  const rhIdx = findC(
    h,
    "ReferenceHourlyRate",
    "referenceHourlyRate",
  );
  const crIdx = findC(h, "CostsRatio", "costsRatio");
  const ciIdx = findC(h, "ClaimAmount", "claimAmount");
  const sfIdx = findC(h, "SuccessFeeRate", "successFeeRate");
  const cliIdx = findC(h, "ClientIds", "clIds");
  const opiIdx = findC(h, "OpponentIds", "opIds");
  const oliIdx = findC(h, "OpponentLawyerIds", "olIds");
  const exiIdx = findC(h, "ExpertIds", "exIds");
  const cpiIdx = findC(h, "ClientContactIds", "cpIds");
  const ociIdx = findC(h, "OpponentContactIds", "ocIds");
  const meiIdx = findC(h, "MediatorIds", "meIds");
  const ariIdx = findC(h, "ArbitratorIds", "arIds");
  const op2iIdx = findC(h, "Op2Ids", "op2Ids");
  const colIdx = findC(h, "CaseCollaborators", "caseCollaborators");
  const stgIdx = findC(h, "Stages", "stages");
  const docIdx = findC(h, "Docs", "docs");
  // 209 §19 [ADVISOR] — the case's language, under its header in any of the three
  // languages; a code or the form's own name for it (caseLanguage.ts). A row without
  // one creates the case « à choisir », and never clears a stored one on an update.
  const lgIdx = findC(h, "Language", "Langue", "Taal");
  const pn = (v: any) => {
    if (v === "" || v == null) return null;
    const n = parseFloat(String(v).replace(",", "."));
    return isNaN(n) ? null : n;
  };
  const pIds = (v: any) =>
    String(v || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => +x)
      .filter((n) => !isNaN(n));
  const pJson = (v: any) => {
    if (!v) return [];
    try {
      return JSON.parse(v);
    } catch {
      return [];
    }
  };
  let imported = 0,
    updated = 0,
    skipped = 0;
  // 214 addendum 5 §29 [ADVISOR — the clients sheet's rule] — the rows refused (no Ref, an Id naming no matter of the
  // list): counted with the skipped ones, each with its reason in the summary
  const refused: RefusedRow[] = [];
  // 05n §4 — one resolver, one report, per import run. `cell()` ALWAYS returns
  // what should be written, so no call site has to remember which states mean
  // "write it anyway"; the report is what tells somebody it needs a human.
  const vocabReport = makeImportVocabularyReport();
  // A10 (brief 65) — src/lib/importCell.
  const cell = makeImportCell(vocabReport);
  let working = mode === "replace" ? [] : [...ca];
  /* 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — WHAT THIS IMPORT WOULD LOSE, worked out before anything is
     written: the plan the window shows (a value emptied, an entry taken out of a list, a matter archived), and —
     beside `working` — the rows « Importer sans vider » would write (an empty cell leaving the stored value, what a
     cell states still applying). « Replace all » archives every stored matter (the lists' delete is the archive,
     App.tsx `_apiEnt`) and creates the sheet's rows anew under the same refs: each stored matter is listed. The
     sheet's own headers name the fields; a list entry is named from the view's pools where one holds it. */
  const losses = newLossPlan();
  const workingKeep: unknown[] = mode === "replace" ? [] : (ca as unknown[]).slice();
  if (mode === "replace") addRowsTaken(losses, ca, "archived", matterLabel, matterTitle);
  const names = poolNamer(pools, pname);
  const hdr = (i: number, name: string): string => (i >= 0 ? String(h[i]) : name);
  const LIST_FIELDS: DiffField[] = [
    ["title", hdr(ttIdx, "Title")], ["ctype", hdr(tpIdx, "Type")], ["openDate", hdr(odIdx, "OpenDate")], ["desc", hdr(deIdx, "Description")],
    ["juId", hdr(juIdx, "JurisdictionId")], ["responsiblePartners", hdr(rpIdx, "ResponsiblePartnerIds")], ["referenceHourlyRate", hdr(rhIdx, "ReferenceHourlyRate")],
    ["costsRatio", hdr(crIdx, "CostsRatio")], ["claimAmount", hdr(ciIdx, "ClaimAmount")], ["successFeeRate", hdr(sfIdx, "SuccessFeeRate")],
    ["clIds", hdr(cliIdx, "ClientIds")], ["opIds", hdr(opiIdx, "OpponentIds")], ["ocuIds", hdr(oliIdx, "OpponentLawyerIds")], ["exIds", hdr(exiIdx, "ExpertIds")],
    ["cpIds", hdr(cpiIdx, "ClientContactIds")], ["ocIds", hdr(ociIdx, "OpponentContactIds")], ["meIds", hdr(meiIdx, "MediatorIds")], ["arIds", hdr(ariIdx, "ArbitratorIds")],
    ["op2Ids", hdr(op2iIdx, "Op2Ids")], ["caseCollaborators", hdr(colIdx, "CaseCollaborators")], ["stages", hdr(stgIdx, "Stages")], ["docs", hdr(docIdx, "Docs")],
  ];
  const languageOf = (cellValue: unknown): { language?: string } => {
    const language = importedCaseLanguage(cellValue, available);
    return language ? { language } : {};
  };
  let rn = 1;
  for (const row of rows) {
    rn++;
    const vals = h.map((k) => row[k]);
    const ref = String(vals[refIdx] || "").trim();
    const title = String(vals[ttIdx] || "").trim();
    /* 214 addendum 5 §29 [RULED 65 — Laurent, 05/10] — WHERE THE ROW LANDS, asked ONCE and read by the window, the
       write and the new row alike: by its Ref (its Id not read); else by its Id; else a new matter. « Replace all »
       and a sheet without the Id column read no Id. A row without a Ref whose Id names no matter is refused. */
    const match = sheetRowMatch(working, ref, mode === "replace" || idIdx < 0 ? undefined : vals[idIdx]);
    if (match.by === "refused") {
      refused.push({ rn, id: match.id });
      skipped++;
      continue;
    }
    if (match.by === "new" && !ref && !title) {
      skipped++;
      continue;
    }
    const full = {
      ref,
      title,
      status: cell("case_status", _vCaseStatus, vals[stIdx], rn) || "active",
      ctype: cell("case_type", _vCaseType, vals[tpIdx], rn),
      openDate: String(vals[odIdx] || "").trim(),
      desc: String(vals[deIdx] || "").trim(),
      juId: vals[juIdx] ? +vals[juIdx] || null : null,
      responsiblePartners: pIds(vals[rpIdx]), // 214 addendum 3 §21 — every partner the cell names
      referenceHourlyRate: pn(vals[rhIdx]),
      costsRatio: pn(vals[crIdx]),
      claimAmount: pn(vals[ciIdx]),
      successFeeRate: pn(vals[sfIdx]),
      clIds: pIds(vals[cliIdx]),
      opIds: pIds(vals[opiIdx]),
      // 56 §2 — `ocuIds`, and THE IMPORT IS THE END THAT MOVED. `olIds` is the
      // pre-2026-04-06 name; the server's alias map (cases.js
      // normalizeJunctionAliases) knows the other eight legacy names and has
      // never known this one, so this column has never been applied — ignored
      // on a merge (the stored value wins), dropped on create/replace.
      //
      // The SPREADSHEET is untouched: `findC` above still matches the
      // "OpponentLawyerIds" header and its "olIds" alternate, so a file
      // exported before today still imports. What changed is the payload key,
      // which is internal.
      //
      // Teaching the server `olIds` instead would have been the other end, and
      // it is worse: it gives ONE family two legacy names where the other eight
      // have one, and a merge body carries BOTH `ocuIds` (from the shape) and
      // `olIds` (from the row) — so the map would have to arbitrate between a
      // stored value and an imported one, an ambiguity that does not exist
      // today and that no other family would share.
      ocuIds: pIds(vals[oliIdx]),
      exIds: pIds(vals[exiIdx]),
      cpIds: pIds(vals[cpiIdx]),
      ocIds: pIds(vals[ociIdx]),
      meIds: pIds(vals[meiIdx]),
      arIds: pIds(vals[ariIdx]),
      op2Ids: pIds(vals[op2iIdx]),
      caseCollaborators: pJson(vals[colIdx]),
      stages: pJson(vals[stgIdx]),
      docs: pJson(vals[docIdx]),
      ...languageOf(vals[lgIdx]),
    };
    const eIdx = match.by === "new" ? -1 : match.at; // 214 addendum 5 §29 — the one answer (was: by Ref alone)
    /* 214 addendum 3 §24 [RULING 62 (b) — Laurent, 04/10] — A COLUMN THE SHEET DOES NOT HAVE LEAVES THE STORED VALUE
       AS IT IS; an empty cell in a column the sheet has still clears it (the clients sheet's rule, 164 §A.1). Until
       this every column the sheet lacked was written as its default over the stored row — a partial sheet (Ref,
       Title, Status) emptied the parties, the dates, the description and the rates of every matter it named, and a
       sheet without Status set every one « active » (docs/214-deploy-note.md §7.24). A new row keeps the defaults. */
    // 214 addendum 4 §25 [ADVISOR] — an EMPTY Status cell leaves an existing matter's status (a status is never
    // emptied; « active » stays a new matter's default): for a stored row the column counts as absent.
    const statusStated = String(vals[stIdx] ?? "").trim() !== "";
    const data = eIdx >= 0
      ? withoutAbsentColumns(full, [
          // 214 addendum 5 §29 — a row found by its Id never writes its empty Ref over the stored one: the Ref is the
          // key, the Id stood in for it (an empty cell would write "" over a NULL ref — docs/214-deploy-note.md §7.29)
          ["ref", match.by === "id" ? -1 : refIdx],
          ["title", ttIdx], ["status", statusStated ? stIdx : -1], ["ctype", tpIdx], ["openDate", odIdx], ["desc", deIdx], ["juId", juIdx],
          ["responsiblePartners", rpIdx], ["referenceHourlyRate", rhIdx], ["costsRatio", crIdx], ["claimAmount", ciIdx],
          ["successFeeRate", sfIdx], ["clIds", cliIdx], ["opIds", opiIdx], ["ocuIds", oliIdx], ["exIds", exiIdx],
          ["cpIds", cpiIdx], ["ocIds", ociIdx], ["meIds", meiIdx], ["arIds", ariIdx], ["op2Ids", op2iIdx],
          ["caseCollaborators", colIdx], ["stages", stgIdx], ["docs", docIdx],
        ])
      : full;
    if (eIdx >= 0) {
      const stored: unknown = working[eIdx];
      // 214 addendum 4 §25 — the row's losses under its ref, and the row « sans vider » would write (addendum 5 §29: a
      // row found by its Id, under its matter's ref, else its title)
      const gKey = match.by === "id" ? `#${String(match.id)}` : ref;
      const gLabel = match.by === "id" ? matterLabel(stored) : ref;
      for (const line of rowLosses(stored, data, LIST_FIELDS, names)) addLoss(losses, gKey, gLabel, line);
      working[eIdx] = { ...working[eIdx], ...data };
      workingKeep[eIdx] = { ...(stored as object), ...withoutEmptying(data, stored) };
      updated++;
    } else {
      const row = { id: tempId(), ...data };
      working.push(row);
      workingKeep.push(row);
      imported++;
    }
  }
  // 214 addendum 5 §29 — each refused row's reason, in the summary's own toast (one toast at a time — 212 [METHOD] (a))
  const refusedText = refusedReasons(t as Translate, refused);
  if (!imported && !updated) {
    toast(
      t("toast.no_valid_rows", { fallback: "⚠ No valid rows" }) +
        " (" +
        t("cc.import.n_skipped", withCount(skipped, "n", { fallback: skipped + " skipped" })) +
        ")" +
        refusedText,
    );
    setCaImportFile(null);
    return;
  }
  /* 214 addendum 4 §25 [RULED 63] — THE WINDOW when something would be lost, the import at once when nothing would. */
  const commitPlan = (rows: unknown[]) => {
  s(SK.ca, rows, sCa);
  const _vocabMsg = vocabReport.summary(t);
  if (_vocabMsg) toast("\u26a0 " + _vocabMsg);
  toast(
    t("cc.import.toast_done", {
      imported,
      updated,
      skip:
        skipped > 0
          ? ", " +
            t("cc.import.n_skipped", withCount(skipped, "n", { fallback: skipped + " skipped",
            }))
          : "",
      fallback: `Imported ${imported}, updated ${updated}${skipped > 0 ? ", skipped " + skipped : ""}`,
    }) + refusedText,
    refused.length > 0 ? "warning" : undefined, // 214 addendum 5 §29 [ADVISOR] — a refused row: the warning colour
  );
  setCaImportFile(null);
  };
  if (hasLoss(losses)) {
    setLossAsk({ plan: losses, run: (keep) => { setLossAsk(null); commitPlan(keep ? workingKeep : working); } });
    return;
  }
  commitPlan(working);
};
if (error)
  return (
    <ImportErrorModal error={error} setCaImportFile={setCaImportFile} t={t} />
  );
if (!parsed)
  return (
    <ImportLoadingModal t={t} />
  );
if (lossAsk)
  return (
    <ImportLossDialog
      plan={lossAsk.plan}
      t={t as Translate}
      groupsSentence={(t as Translate)("import.loss.matters", withCount(lossAsk.plan.groups.length, "count", { fallback: `${String(lossAsk.plan.groups.length)} matters` }))}
      onAll={() => { lossAsk.run(false); }}
      onKeep={hasEmptied(lossAsk.plan) ? () => { lossAsk.run(true); } : undefined}
      onCancel={() => { setLossAsk(null); }}
    />
  );
return (
  <ModalLayout
    title={t("modal.import.cases_title", {
      fallback: "Import Cases",
    })}
    onClose={() => setCaImportFile(null)}
    onSave={doImport}
    saveLabel={t("modal.import.confirm_n_cases", {
      count: rows.length,
      fallback: "Import {count} cases",
    })}
    cancelLabel={t("common.cancel", { fallback: "Cancel" })}
    showX={false}
    closeOnOverlay
    maxWidth={600}
  >
        <p style={{ fontSize: FS.s11, marginBottom: 10 }}>
          {t("app.import.found_rows", withCount(rows.length, "n", { fallback: `Found ${rows.length} rows.`,
          }))}
        </p>
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 600,
              color: "var(--tm)",
              marginBottom: 6,
            }}
          >
            {t("case.import.mode_label", {
              fallback: "Import mode",
            })}
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              cursor: "pointer",
              marginBottom: 4,
            }}
          >
            <input
              type="radio"
              checked={mode === "add"}
              onChange={() => setMode("add")}
            />
            {t("cc.import.cc_add_update", {
              fallback: "Add new, update existing (match by Ref)",
            })}
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              checked={mode === "replace"}
              onChange={() => setMode("replace")}
            />
            <span>
              {t("case.import.replace_all", {
                fallback: "Replace all cases",
              })}{" "}
              <span style={{ color: "var(--rd)", fontSize: FS.s10 }}>
                {t("app.import.destructive", { fallback: "(destructive)" })}
              </span>
            </span>
          </label>
        </div>
        <div
          style={{
            fontSize: FS.s10,
            fontWeight: 600,
            color: "var(--tm)",
            marginBottom: 4,
          }}
        >
          {t("cc.import.preview", withCount(Math.min(5, rows.length), "n", { fallback: `Preview (first ${Math.min(5, rows.length)} rows)`,
          }))}
        </div>
        <div
          className="table-wrap"
          style={{ maxHeight: 200, overflowY: "auto" }}
        >
          <Table style={{ width: "100%" }}>
            <thead>
              <tr>
                {hdrs.slice(0, 7).map((h2) => (
                  <th
                    key={h2}
                    style={{
                      fontSize: FS.s8,
                      padding: "3px 4px",
                      color: "var(--tm)",
                    }}
                  >
                    {h2}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 5).map((row, i) => (
                <tr key={i}>
                  {hdrs.slice(0, 7).map((h2) => (
                    <td
                      key={h2}
                      style={{
                        fontSize: FS.s9,
                        padding: "2px 4px",
                        maxWidth: 100,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {String(row[h2] || "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
  </ModalLayout>
);
}

const CaView = ({
  did,
  sDid,
  caMode,
  setCaMode,
  ca,
  sCa,
  cl,
  sCl,
  op,
  ocu,
  ex,
  cp,
  oc,
  me,
  ar,
  op2,
  co,
  ju,
  te,
  sTe,
  ag,
  sAg,
  tk,
  sTk,
  tkTypes,
  evtTypes,
  schedAxis,
  holidays, // 132 §6
  calDisp, // 133 §4
  agTypeFilt,
  setAgTypeFilt,
  cf,
  sCf,
  f,
  sF,
  mod,
  showDL,
  sShowDL,
  lookback,
  showEarlier,
  sShowEarlier,
  showDesc,
  sShowDesc,
  currentUser,
  // Phase 39 — click-to-call on the case-team badges (ring / add-to-call).
  onCallColleague,
  callActive = false,
  partiesLocked,
  setPartiesLocked,
  partiesOpen,
  setPartiesOpen,
  undoEntry,
  setUndoEntry,
  collStg,
  setCollStg,
  expCl,
  sExpCl,
  clSrch,
  sClSrch,
  caTesel,
  setCaTesel,
  agMy,
  sAgMy,
  caSort,
  setCaSort,
  tkStatusFilt,
  setTkStatusFilt,
  chatOpen,
  setChatOpen,
  chatMsgs,
  setChatMsgs,
  chatInput,
  setChatInput,
  chatType,
  setChatType,
  chatSearch,
  setChatSearch,
  chatMention,
  setChatMention,
  chatMentionQ,
  setChatMentionQ,
  chatRead,
  setChatRead,
  gChat,
  setGChat,
  activeTimer,
  activeTimers,
  setMailDlg,
  mtgDefLoc,
  setDoodleDlg,
  permMx,
  chatRef,
  undoTRef,
  s,
  nav,
  tryNavCase,
  om,
  cm,
  del,
  toast,
  etTag,
  evtLongestCode, // 139 §1 — travels with etTag, to CaseAgendaSection
  coAvs,
  toggleDL,
  locIcon,
  svTkData,
  // Phase 29 C61 — subtask done-tick hands (task, subtask) to App's
  // complete-style prompt (time-log offer).
  onSubtaskDone,
  delTk,
  TkStatusTag,
  startTimer,
  getTimerDisplay,
  evToVevent,
  downloadIcs,
  keepScroll,
  sQm,
  sQf,
  OPEN_ST,
  MultiSelect,
  linkTypesList,
  caseTypesList,
  caseStatusesList,
  docStatusesList,
  stageTypesList,
  taskStatusesList,
  itemLevelList,
  reminderOptsList,
  setPartyDetailDlg,
  inv,
  sInv,
  firmSettings,
  partyRoleEntries,
  /* 174 §5 — the email section's matter-choice modal picks over BOTH lists
     and reads the same recents every other MatterPicker host does. */
  cc = [],
  getRecentCases,
  // 176 §3.2 — forwarded to CaseDocumentsSection untouched (App.tsx:9588).
  onProvisionFolders,
  // 212 §27 — the matters' subfolder check, handed on to the list's toolbar (absent: no button)
  onFolderCheck,
  // 190 round 6 [T] — App's quadrant resolver, for the case Bento's card edges.
  userMatrixQuadrant,
}: any) => {
  const _docStatuses =
    docStatusesList && docStatusesList.length ? docStatusesList : DOC_STATUSES;
  /* 151 §2 step 3 — document_category for the one modal's Type select. Local
     like _caseStatusEntries below (the module cache makes this the same feed
     App holds), because App threads no docTypesList to this view. */
  const _docTypes = useTaxonomyList("document_category");
  // 05n — DEAD here too: declared, never read. `stageTypesList` itself IS
  // read, at :3359, where it is passed on to CaseDocumentsSection.
  const _taskStatuses =
    taskStatusesList && taskStatusesList.length
      ? taskStatusesList
      : TASK_STATUSES;
  const _reminderOpts =
    reminderOptsList && reminderOptsList.length
      ? reminderOptsList
      : REMINDER_OPTS;
  const _ITEM_LEVEL = itemLevelMap(itemLevelList);
  const { t } = useTranslation();
  // 214 addendum 1 §11 [RULED 57] — the reader's locale, for the margin's percentage in the reader's number format (`pct1`).
  const __locale = (useTranslation() as { locale: string }).locale;
  /* Phase 30 Stage-3 (C-1) — the 14-day calendar lists firm-visible events
     across cases; a click on one whose case is off-scope dead-jumped onto a
     blank CaseView. C85 guard instead. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  // Phase M+3.7 Stage 3 round 3 — raw entries for case_status / case_type so
  // header badges can render in the user's language (resolveTaxonomyLabel
  // reads window.__lxLang via pickEntryLabel). Reuses the same module-level
  // cache as caseStatusesList / caseTypesList from App.jsx.
  const _caseStatusEntries = useTaxonomy("case_status").entries;
  /* 189 §B — the firm's own field definitions; the ones that say "show in
     lists" become the case list's trailing columns. */
  const _customFieldTax = useTaxonomy("custom_field") as { entries: unknown; lang: string };
  const customFieldCols = customFieldColumns(_customFieldTax.entries, "case", _customFieldTax.lang);
  /* 123 §3 — the firm's own first meeting type, for the parties-pane meeting
     button below. `null` when the firm has flagged none, and the seed then
     leaves the type empty rather than naming a code the taxonomy may not have. */
  const _meetingType = firstMeetingTypeCode(useTaxonomy("event_type").entries);
  const _caseTypeEntries = useTaxonomy("case_type").entries;
  const _caseStageEntries = useTaxonomy("case_stage").entries;
  // Brief 57 §1 — raw rows, not the tuple list: `meta.terminal` is what says
  // whether a status is open, and tuples carry no meta.
  const _taskStatusEntries = useTaxonomy("task_status").entries;
  const [caSearchQ, setCaSearchQ] = useState("");
  // C.2 — the case financial summaries + success fee moved server-side
  // (POST /api/financials/compute; same arithmetic, verbatim,
  // recording-verified: recordings/c2-financials/). The view posts the rows
  // it holds and displays the returned money — it computes none.
  // Latest-wins: a stale response never overwrites a newer one. The previous
  // result stays on screen until the next response lands ("—" placeholders
  // before the first); results are keyed by case id so a case switch never
  // shows another case's money.
  const { rows: _roleRows } = useRoleLabels();
  const [finCalc, setFinCalc] = useState<any>(null);
  // 161 §8/§9 — the open matter's OVERHEADS share (R9), served, an input to
  // the recap's gross margin. Absent (route down, sheet empty) is never zero:
  // the recap then shows "—" and margin stays null (§4.2).
  const [ohShare, setOhShare] = useState<Record<number, number>>({});
  useEffect(() => {
    if (!did) return;
    let live = true;
    getOverheadsShare(did, null)
      .then((r: any) => {
        if (live && r && Number.isFinite(+r.share)) setOhShare((p) => ({ ...p, [did]: +r.share }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [did]);
  const _fcSeq = useRef(0);
  useEffect(() => {
    const seq = ++_fcSeq.current;
    computeCaseFinancials({
      cases: (ca || []).map(redMatterFin),
      entries: (te || []).map(redEntryFin),
      collaborators: (co || []).map(redCoFin),
      roleRows: redRoleRows(_roleRows),
      // Brief 87 §3b — the invoices whose stored vatRate prices their own
      // entries. Absent, everything takes the firm's current rate.
      invoices: (inv || []).map(redFinInv),
      // 161 §8 — the recap's DOCUMENT basis (R7) + the overheads input (R9).
      matterInvoices: (inv || []).map(redRecapInv),
      overheadsByCase: ohShare,
    })
      .then((r: any) => {
        if (_fcSeq.current !== seq) return;
        const byId: any = {};
        for (const row of r?.perCase || []) byId[row.id] = row;
        setFinCalc(byId);
      })
      .catch(() => {
        if (_fcSeq.current === seq) setFinCalc(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ca, te, co, _roleRows, inv, ohShare]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [collabLocked, setCollabLocked] = useState(true);
  // Phase 28 (E) — lock affordance for the Access-rights matrix in the
  // consolidated "Case Team" section. Default locked, like collabLocked.
  // Phase 37 S3 correction 1 — viewing + editing panes merged into one matrix,
  // so a single lock now governs the whole rights area.
  const [teamViewLocked, setTeamViewLocked] = useState(true);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [agLocked, setAgLocked] = useState(true);
  const [tkLocked, setTkLocked] = useState(true);
  const [docLocked, setDocLocked] = useState(true);
  const [stgLocked, setStgLocked] = useState(true);
  // 186 (e) [RULED 186 (16)] — the Stages search field's query; the list is
  // filtered here, before its page, by CaseStageTimeline's own predicate.
  const [stgQ, setStgQ] = useState("");
  const [tkSort, setTkSort] = useState<any>({});
  const [tkExpanded, setTkExpanded] = useState(() => new Set());
  // Phase 23 (D2-corr) — inline event-subtask expansion (agenda pane).
  const [evExpanded, setEvExpanded] = useState(() => new Set());
  const toggleEvExpanded = (id: any) =>
    setEvExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const [tkMy, setTkMy] = useState(false);
  const [tkMatrix, setTkMatrix] = useState(false);
  /* 190 glance round 2 [A] — the case Bento's open state lives beside the
     matrix's: the Tasks section holds the toggle and the pane, the Agenda
     section hides its body under the pane. */
  const [caseBento, setCaseBento] = useState(false);
  const [tkPage, setTkPage] = useState(0);
  const [agPage, setAgPage] = useState(0);
  const [agWeek, setAgWeek] = useState("this");
  // 186 close (6) — the Stages pager's state is the shared hook's (was `stgPage`).
  const stgPager = usePager();
  const [coPage, setCoPage] = useState(0);
  const [crPage, setCrPage] = useState(0);
  // 86 Phase 2 step 7 — the assigned-collaborator picker's open flag. It was a
  // `_open` expando on a DOM node plus a `.addCoDD` querySelector; CCV had
  // already made it state (`CollectiveCaseView.tsx:2430`), and a shared
  // component cannot reach into the DOM for a sibling it does not own.
  const [addCoOpen, setAddCoOpen] = useState(false);
  const [tePage, setTePage] = useState(0);
  const [tlPage, setTlPage] = useState(0);
  // Phase 28 (item B) — append-only chronology rows for this case's timeline.
  // Re-fetched when the case is opened or after activity that produces log
  // rows (case edit → cs.updatedAt; task/event create → list length changes).
  /* Round 19 (C104) — the fetch is GATED on the viewer's actual case
     visibility (his server-scoped `ca` list — the same source of truth the
     server's gate resolves): `did` can hold a case OUTSIDE that list (an
     unguarded jump from a notification / chat item, a stale selection), and
     the old did-only guard then fired /chronicle into a guaranteed 403 —
     console noise on every te/ag/tk dep change while the user sat there.
     Race-safe per the C103 pattern: the membership boolean is an explicit
     dep, so when the boot lists arrive the effect re-runs and an ENTITLED
     fetch fires then — deferred, never skipped. */
  const _chronVisible = did != null && ca.some((c: any) => c.id === did);
  /* 211 §10 — asked the 210 §1 way (src/hooks/usePageList.ts): a failure is
     asked again twice, then the timeline says it could not load, with
     « Réessayer » — it answered `[]` here, and the timeline counted short.
     The refresh is what the effect re-read on: the case's `updatedAt`, the
     three lengths. */
  const _chron = usePageList<any>(
    PAGE_LISTS.chronicle,
    "the case's chronicle",
    _chronVisible ? "/cases/" + String(did) + "/chronicle" : null,
    readList,
    [ca.find((c: any) => c.id === did)?.updatedAt, te.length, ag.length, tk.length].map(String).join("|"),
  );
  const chronicle = _chron.rows;
  const _chronLists = usePageListContext(_chron.entry);
  useEffect(() => {
    setTkPage(0);
    setAgPage(0);
    stgPager.setPage(0);
    setCoPage(0);
    setCrPage(0);
    setTePage(0);
    setTlPage(0);
  }, [tkMy, tkStatusFilt, did]);
  const __viewParty = (it: any, ck: any) => {
    if (!setPartyDetailDlg) return;
    setPartyDetailDlg({ type: ck ? ck.replace(/Ids$/, "") : null, item: it });
  };
  // Phase 13 Item 4 — ephemeral per-section collapse map. Defaults match the
  // brief: Description / Agenda / Tasks / Stages / Emails / Time-entries open;
  // everything else collapsed. No persistence — re-collapses on reload.
  const [secCol, setSecCol] = useState<any>({ ...SEC_COL_DEFAULTS });
  const sCol = (k: any) => !!secCol[k];
  const tog = (k: any) => setSecCol((p: any) => ({ ...p, [k]: !p[k] }));
  const [pfMod, setPfMod] = useState<any>(null);
  /* 214 §3 [RULED 52] — the suspend window (one for a counsel's link and for a client's own counsel's), and the
     role's word for a contact with the role Avocat (the contact_role vocabulary; the hook's cache is module-level). */
  const [suspendDlg, setSuspendDlg] = useState<{ kind: "ocu" | "cp"; id: number; name: string } | null>(null);
  const contactRoleEntries = useTaxonomy("contact_role").entries;
  const [caMy, setCaMy] = useState(false);
  /* 139 §2.3 — the prospect axis, beside the my-pill state whose shape it
     copies. "both" is the ruled default; Laurent adjusts at the glance. */
  const [caProspect, setCaProspect] = useState<ProspectFilterValue>("both");
  const [stgExp, setStgExp] = useState(() => new Set());
  const [caListLocked, setCaListLocked] = useState(true);
  /* BRIEF 108 §5 — locks re-lock when the viewed matter changes. This
     component resets its locks on unmount (leaving the section), but it does
     NOT unmount switching from case A to case B — or between the list
     (did == null) and a case — so every pane lock survived those moves. One
     effect, keyed on the matter the rule names. The list lock re-locks on the
     same key: drilling into a case is leaving the list page. */
  useEffect(() => {
    setCollabLocked(true);
    setTeamViewLocked(true);
    setAgLocked(true);
    setTkLocked(true);
    setDocLocked(true);
    setStgLocked(true);
    setCaListLocked(true);
    /* 110 §6a — the stage drawers' open-state clears on the same key. It is
       a Set of bare stage ids, stage ids are per-case JSON integers (1, 2, …
       — measured colliding across matters on staging), and this component
       does not unmount between matters: a docs drawer opened on case A
       rendered OPEN on any case sharing the id — the glance's "permanent"
       empty-docs line. The timeline's own notesOpen clears via the mount key
       beside it. */
    setStgExp(new Set());
    /* 190 round 9 [AT] [RULED, Laurent 2026-09-17 — "opening/collapsing a
       section should not be app wide, it's just when viewing that CV/CCV"]:
       the collapse map takes the same treatment as the locks above — back to
       the defaults when the viewed matter changes. It had been ONE map for
       the component since Phase 13 (#49, 2026-05-24) and never keyed on the
       matter, and this component does not unmount between matters (108 §5),
       so a section opened on case A was open on case B. Long-standing, not a
       regression: `git log -S` finds no later change to it. */
    setSecCol({ ...SEC_COL_DEFAULTS });
  }, [did]);
  const [caImportFile, setCaImportFile] = useState<any>(null);
  const caFileRef = useRef<any>(null);
  // Phase M+2b — usePermissions hook + per-case RP fetch.
  // Top-level isPartnerStrict has no caseId scope; admin-only by default
  // since caseView.terms preset default is false everywhere.
  const { can: _permCan, viewer: _permViewer, finVisible: _permFin, billingDelegate: _permBillingDelegate } = usePermissions();
  /* 190 §D — the list Bento's hook, at the top with the other hooks (rules
     of hooks: the list block below sits after the detail screen's early
     return). It fetches only while the board is showing. */
  const listBento = useBento("list", "case", caMode === "board");
  /* 190 round 6 [S](i) — MEASURED FIRST: this host already holds the other
     kind's rows (`cc`, App hands `collectiveCases` to both views), so no
     fetch is added. When the chosen template spans both kinds, the SAME hook
     over the other kind (the choice is per surface, so both agree on the
     template) reads and writes the collective cases' own placements. */
  const listBentoOther = useBento("list", "cc", caMode === "board" && listBento.allMatters);
  const [_caseRPMap, _setCaseRPMap] = useState<any>({});
  useEffect(() => {
    if (!did) return;
    // Server enforces visibility (404 if the case isn't in the viewer's
    // getVisibleCaseIds set). FE guard removed in hotfix #2 — admin and RP
    // viewers should always be able to fetch RPs for cases they can see.
    let cancelled = false;
    api
      .get("/cases/" + did + "/responsible-partners")
      .then((rps) => {
        if (cancelled) return;
        const ids = new Set(
          (Array.isArray(rps) ? rps : []).map((r) => r.collaboratorId),
        );
        _setCaseRPMap((prev: any) => ({ ...prev, [did]: ids }));
      })
      .catch(() => {
        /* silent — RP fetch failure leaves overlay false */
      });
    return () => {
      cancelled = true;
    };
  }, [did]);
  const _isRPForCase = (caseId: any) =>
    !!(
      _caseRPMap[caseId] &&
      _permViewer &&
      _caseRPMap[caseId].has(_permViewer.id)
    );
  const canFin = !!_permViewer?.isAdmin || _permCan("caseView.financials.view");
  const isPartnerStrict = !!_permViewer?.isAdmin || _permCan("caseView.terms.view");
  // 55 §3 row 21 — the date that belongs to the CURRENT status, for the list
  // column beside the pill. `active` returns null on purpose: its date is the
  // Opening date, which already has its own column; the modal and the info
  // pane are where all four live together.
  const statusDate = (c: any) =>
    c.status === "pending"
      ? c.pendingDate || null
      : c.status === "closed"
        ? c.closedDate || null
        : c.status === "archived"
          ? c.archivedDate || null
          : null;
  // 55 row 22 — `marginPctClr` went with the Margin % column; its only call
  // site was that cell.
  useEffect(() => {
    if (did) {
      // Phase 19 r9 — last-mile fix for "open linked event" from Places.
      // The agenda section is open by default, but it defaults to the
      // current-week filter (agWeek="this") + page 0, so a linked event in
      // another week / page isn't in the DOM when scrollToEv fires and the
      // scroll silently fails. If a scroll-to-event is queued AND that event
      // belongs to THIS case, clear the week filter + "My" filter and jump to
      // the event's page BEFORE checkScrollToEv runs, so #ev-<id> renders.
      const pendingEv = peekScrollToEv();
      if (pendingEv != null) {
        const evRow = (ag || []).find((a: any) => a.id === Number(pendingEv) || a.id === pendingEv);
        if (evRow && evRow.caId === did) {
          setAgWeek("all");
          sAgMy(false);
          const sorted = (ag || [])
            .filter((a: any) => a.caId === did && a.date)
            .sort((a: any, b: any) => (new Date(a.date) as any) - (new Date(b.date) as any));
          const idx = sorted.findIndex((a: any) => a.id === evRow.id);
          if (idx >= 0) setAgPage(Math.floor(idx / 10));
        }
      }
      checkScrollToEv();
      checkScrollToTk();
      setTkSort((p: any) => {
        if (!p[did]) return p;
        const n = { ...p };
        delete n[did];
        return n;
      });
    }
  }, [did]);
  const { confirmAndUndo, confirmDlg, cancelConfirm, undoToast, runUndo } =
    useConfirmAndUndo();
  const caRef = useRefLatest(ca);
  const agRef = useRefLatest(ag);
  const teRef = useRefLatest(te);
  const tkRef = useRefLatest(tk);
  const chatMsgsRef = useRefLatest(chatMsgs);
  // Stage delete snapshots the stage object only. Child docs whose stageId
  // points at the deleted stage are NOT mutated — they orphan into the General
  // Documents bucket for the 5s window and re-home under the stage on restore.
  if (did) {
    const cs = ca.find((c: any) => c.id === did);
    if (!cs) return null;
    const _isRPHere = _isRPForCase(cs.id);
    // Brief 86 §2a-2 — [RULED] financials are the RP's: settable in Settings,
    // or granted per case in the Case Team section's access rights. That is
    // the server's isFinancialVisible() and this is its mirror; the section
    // key path it replaces read a different grant table from the one the
    // server consults, so a responsible partner was financially visible to
    // every route and shown no Financial summary. The old key is still
    // honoured, as a widening only — a firm that had ticked
    // caseView.financials.view for a role keeps what it granted.
    const canFin =
      _permFin({ caseId: cs.id }) ||
      _permCan("caseView.financials.view", { caseId: cs.id });
    const canTerms = _permCan("caseView.terms.view", { caseId: cs.id });
    // Brief 86 §2a-2 — [RULED] the time entries section is visible to EVERY
    // collaborator who can open the case, "so one knows who is working and did
    // what". It is the deliberate counterpart of the line above: TIME is open,
    // MONEY is the RP's — and the money columns inside this section are
    // scrubbed server-side by the same isFinancialVisible() canFin mirrors
    // (lib/shapes.js filterAndShapeTimeEntries), so opening the section cannot
    // open the rates. Reaching this component is itself the case-access check.
    const canTimeEntries = true;
    const canEditOthersTasks = _permCan("caseView.tasks.edit", { caseId: cs.id });
    const canAssignCollabs = _permCan("caseView.identity.edit", { caseId: cs.id });
    const canViewTimeline = _permCan("caseView.timeline.view", { caseId: cs.id });
    // 182 A.3 — the Notes section's bits (caseView.notes.*), the tasks shape;
    // 186 B.2 dropped the delete bit's reader (a note is archived, on the edit bit).
    const canViewNotes = _permCan("caseView.notes.view", { caseId: cs.id });
    const canCreateNotes = _permCan("caseView.notes.create", { caseId: cs.id });
    const canEditNotes = _permCan("caseView.notes.edit", { caseId: cs.id });
    // Phase 24 — open-pencil mirrors the server identity-edit rule exactly
    // (caseView.identity.edit + scope), killing the lateral.cases/RP "theater".
    const canModifyCase = _permCan("caseView.identity.edit", { caseId: cs.id });
    /* 150 §5.3 — (b) follows the markBilled KEY's own authority: admin · RP ·
       live per-case grant (the billingDelegate mirror; role defaults are
       deliberately unread — the staging DB carries markBilled=true for every
       role and no server gate reads it). What changes on this surface: a
       granted non-RP DELEGATE gains the strip and the mark buttons — they
       held every server right already; `admin || (can && isRPHere)` was
       excluding exactly them. Ordinary collaborators stay without, as 150 §2
       shipped. */
    const canMarkBilled = _permBillingDelegate({ caseId: cs.id });
    // 150 §4 — InvoiceView's canSeeCards generalised to this scope: admin, or
    // RP of THIS matter. No key — the cards are the RP/admin money summary.
    const canSeeTeCounters = !!_permViewer?.isAdmin || _isRPHere;
    // 150 §5 (d)+(g) — the two new keys; ordinary can() keys, so admin ·
    // role default · live grant. Both arrive ungranted: export/import and the
    // pill column reach non-admins only once a role or a case grant says so.
    const canTeImportExport = _permCan("caseView.timeEntries.importExport", { caseId: cs.id });
    const canTePillColumn = _permCan("caseView.timeEntries.pillColumn", { caseId: cs.id });
    // 144 §4/§5 — the bulk pair, the canMarkBilled shape exactly: admin, or
    // holding the bit (role default or live case grant) while RP of THIS
    // matter — ruling 4's structural bound. A UI hint; the routes re-judge
    // per row.
    const canBulkEdit = !!_permViewer?.isAdmin || (_permCan("caseView.timeEntries.bulkEdit", { caseId: cs.id }) && _isRPHere);
    const canBulkDelete = !!_permViewer?.isAdmin || (_permCan("caseView.timeEntries.bulkDelete", { caseId: cs.id }) && _isRPHere);
    const canManageDocs = _permCan("caseView.documents.edit", {
      caseId: cs.id,
    });
    const canRecordPayments = !!_permViewer?.isAdmin;
    const canViewDesc = _permCan("caseView.identity.view", { caseId: cs.id });
    const canViewParties = _permCan("caseView.parties.view", { caseId: cs.id });
    const canViewCdd = _permCan("caseView.cdd.view", { caseId: cs.id });
    const canViewAgenda = _permCan("caseView.agenda.view", { caseId: cs.id });
    const canEditAgenda = _permCan("caseView.agenda.edit", { caseId: cs.id });
    const canViewTasks = _permCan("caseView.tasks.view", { caseId: cs.id });
    const canEditTasks = _permCan("caseView.tasks.edit", { caseId: cs.id });
    const canDeleteTasks = _permCan("caseView.tasks.delete", { caseId: cs.id });
    const _assignedHere =
      !!_permViewer?.isAdmin ||
      _isRPHere ||
      (cs.caseCollaborators || []).some((x: any) => x && x.coId === currentUser);
    const canViewDocs =
      _assignedHere || _permCan("caseView.documents.view", { caseId: cs.id });
    const canDeleteDocs = _permCan("caseView.documents.delete", {
      caseId: cs.id,
    });
    const canLinkDocs = _permCan("caseView.documents.link", { caseId: cs.id });
    const canViewTime = canTimeEntries;
    const canEditTime = _permCan("caseView.timeEntries.edit", {
      caseId: cs.id,
    });
    const canDeleteTime = _permCan("caseView.timeEntries.delete", {
      caseId: cs.id,
    });
    const canViewCalculator = _permCan("caseView.calculator.view", {
      caseId: cs.id,
    });
    const canEditCalculator = _permCan("caseView.calculator.edit", {
      caseId: cs.id,
    });
    /* 119 (§1c's pre-step) — the nine map-find-filters move to
       `src/lib/matterCast.ts`; the id SOURCES stay here, because they are the
       one thing this host knows and does not share with the collective pane
       (which reads three of them off `ccIds` and its clients off a link table).
       Same nine families, same pools, same order, same names downstream. */
    const { cls, cps, ops, ocs, ols, exs, mes, ars, op2s } = matterCast(
      {
        clIds: cs.clIds,
        cpIds: cs.cpIds,
        opIds: cs.opIds,
        ocIds: cs.ocIds,
        ocuIds: cs.ocuIds,
        exIds: cs.exIds,
        meIds: cs.meIds,
        arIds: cs.arIds,
        op2Ids: cs.op2Ids,
      },
      {
        clients: cl,
        contactPersons: cp,
        opponents: op,
        opponentContacts: oc,
        opponentLawyers: ocu,
        experts: ex,
        mediators: me,
        arbiters: ar,
        additionalOpponents: op2,
      },
    );
    const updCsField = (field: any, val: any) =>
      s(
        SK.ca,
        ca.map((c: any) => (c.id === cs.id ? { ...c, [field]: val } : c)),
        sCa,
      );
    const updCsMerge = (upd: any) =>
      s(
        SK.ca,
        ca.map((c: any) => (c.id === cs.id ? { ...c, ...upd } : c)),
        sCa,
      );
    /* 141 §2.2 — THE PER-MATTER CLIENT STATUS, ON THE CASE'S CLIENT LIST.
       The same affordance the CCV roster has (§2.2), from the same definition:
       `linkStatusCfg` / `linkStatusOptions` in lib/linkStatus.ts, mounted on
       the same shared `StatusPill`.
       IT DOES NOT GO THROUGH `updCsMerge`. Everything else on this pane is a
       field of the case and rides the `s()` engine into the full-row PUT;
       `status` on the link is deliberately NOT a field of that PUT (§2.2 — the
       boundary `isProspect` sits on), so merging it into `cs` and letting the
       engine carry it would write nothing and leave the screen claiming it
       had. One route, called directly.
       ONLY `clLinks` IS MERGED BACK. The route answers with the whole shaped
       case, like the archive and convert routes beside it, but it changed
       exactly one thing — and this view has no form buffer, so spreading the
       server's whole row over the local one could only ever overwrite an edit
       somebody made in the round trip. */
    const clientStatusOptions = linkStatusOptions(t);
    const clientLinkStatus = (clientId: any) => {
      const lk = (cs.clLinks || {})[clientId];
      return (lk && lk.status) || null;
    };
    const setClientStatus = (clientId: any, code: string) => {
      if (clientLinkStatus(clientId) === code) return;
      setCaseClientStatus(cs.id, clientId, code)
        .then((row: any) => {
          if (!row || !row.id) return;
          sCa((prev: any) =>
            Array.isArray(prev)
              ? prev.map((c: any) =>
                  c.id === row.id ? { ...c, clLinks: row.clLinks || {} } : c,
                )
              : prev,
          );
        })
        .catch((e: Error) => toast(e.message, "error"));
    };
    /* 214 §2/§3 [RULED 51–52] — the counsel link's state and the contact link's: their own routes (as the
       client's above), ONLY the two link maps merged back. The parties a counsel stands for ride the case PUT
       (`ocuLinks`), as roles do. Read through typed aliases once (this file's ESLint count may not rise), every
       arrow at most two levels deep. */
    const csL = cs as { id: number; ocuIds?: unknown; cpIds?: unknown; ocuLinks?: LinkMap<CounselLink>; cpLinks?: LinkMap<LinkState> };
    const setCasesRows = sCa as SetRowsFn;
    const toastD = toast as ToastFn;
    const tD = t as TFn;
    const cpPool = cp as readonly unknown[];
    const ocuPool = ocu as readonly unknown[];
    const opsPool = ops as readonly unknown[];
    const olsPool = ols as readonly unknown[];
    const mergeLinks = (answer: unknown) => {
      const row = answer as LinkMapsRow | null;
      if (row && row.id) setCasesRows(withLinkMaps(row));
    };
    const stateErr = (e: Error) => {
      toastD(e.message, "error");
    };
    const setCounselState = (ocuId: number, status: string | null, date?: string) =>
      setCaseCounselStatus(csL.id, ocuId, status, date).then(mergeLinks).catch(stateErr);
    const setContactState = (cpId: number, status: string | null, date?: string) =>
      setCaseContactStatus(csL.id, cpId, status, date).then(mergeLinks).catch(stateErr);
    const ocuLinkOf = (id: number) => linkOf(csL.ocuLinks, id);
    const cpLinkOf = (id: number) => linkOf(csL.cpLinks, id);
    const setCounselParties = (ocuId: number, ids: number[]) => {
      const prev = ocuLinkOf(ocuId);
      updCsMerge({ ocuLinks: { ...(csL.ocuLinks ?? {}), [ocuId]: { status: null, suspendedDate: null, ...(prev ?? {}), partyIds: ids } } });
    };
    const nameIn = (pool: readonly unknown[], id: number) => pname(pool.find((x) => isRowWithId(x, id)));
    const partyOpts = opsPool.filter((o): o is { id: number } => !!o && typeof o === "object" && typeof (o as { id?: unknown }).id === "number").map((o) => ({ id: o.id, name: pname(o) }));
    const lawyerWord = String(resolveTaxonomyLabel("lawyer", contactRoleEntries) ?? "");
    const askSuspend = (kind: "ocu" | "cp", id: number) => {
      setSuspendDlg({ kind, id, name: nameIn(kind === "ocu" ? ocuPool : cpPool, id) });
    };
    const closeSuspend = () => {
      setSuspendDlg(null);
    };
    const confirmSuspend = (date: string) => {
      const d = suspendDlg;
      setSuspendDlg(null);
      if (!d) return;
      if (d.kind === "ocu") void setCounselState(d.id, "Suspended", date);
      else void setContactState(d.id, "Suspended", date);
    };
    const ocuMuted = (id: number) => isSuspended(ocuLinkOf(id));
    const cpMuted = (id: number) => isSuspended(cpLinkOf(id));
    const ocuChipExtra = (id: number) =>
      counselChipExtra({
        link: ocuLinkOf(id),
        parties: partyOpts,
        locked: partiesLocked,
        t: tD,
        onParties: (ids) => {
          setCounselParties(id, ids);
        },
        onSuspend: () => {
          askSuspend("ocu", id);
        },
        onReactivate: () => {
          void setCounselState(id, null);
        },
      });
    const cpChipExtra = (id: number) =>
      isClientCounsel(cpPool.find((x) => isRowWithId(x, id)) as { role?: unknown } | undefined)
        ? clientCounselChipExtra({
            link: cpLinkOf(id),
            locked: partiesLocked,
            t: tD,
            roleLabel: lawyerWord,
            onSuspend: () => {
              askSuspend("cp", id);
            },
            onReactivate: () => {
              void setContactState(id, null);
            },
          })
        : null;
    const ocuSel = actingFirst(counselIdsOf(csL.ocuIds), csL.ocuLinks);
    const cpSel = actingFirst(counselIdsOf(csL.cpIds), csL.cpLinks);
    const rmWithUndo = (field: any, id: any, name: any) => {
      updCsField(
        field,
        (cs[field] || []).filter((x: any) => x !== id),
      );
      if (undoTRef.current) clearTimeout(undoTRef.current);
      setUndoEntry({ field, id, name, caseId: cs.id });
      undoTRef.current = setTimeout(() => setUndoEntry(null), UNDO_WINDOW_MS); // 209 §25
    };
    const doUndo = () => {
      if (!undoEntry) return;
      clearTimeout(undoTRef.current);
      s(
        SK.ca,
        ca.map((c: any) =>
          c.id === undoEntry.caseId
            ? {
                ...c,
                [undoEntry.field]: [
                  ...(c[undoEntry.field] || []),
                  undoEntry.id,
                ],
              }
            : c,
        ),
        sCa,
      );
      setUndoEntry(null);
    };
    const rps = (cs.responsiblePartners || [])
      .map((_id: any) => co.find((c: any) => c.id === _id))
      .filter(Boolean);
    const jr = ju.find((j: any) => j.id === cs.juId);
    const cte = te.filter((t: any) => t.caId === cs.id);
    const meIsAdmin = !!co.find((x: any) => x.id === currentUser)
      ?.isAdmin; /* Phase M+2b: server-side filter (filterAndShapeTimeEntries) already
   trims `te` to entries the viewer can see. The legacy visibleTimeEntries
   client filter is now belt-and-suspenders dead weight — dropped. The
   admin-affordances gate is canMarkBilled (admin or RP); canFin no
   longer triggers mark-billed UI.
   148 §1.1 — and the billed-status filter that stood on top of it is dropped
   too. `cteVis0`/`cteVis` were computed here and READ BY NOTHING: this view
   passed the raw `teBilFilt` down and `CaseTimeEntriesSection` re-derived the
   same narrowing itself. Both the state and this dead pair go; see that
   component for the ruling. */
    const isAdminDet = canMarkBilled;
    const cag = ag
      .filter((a: any) => a.caId === cs.id)
      .sort((a: any, b: any) => (new Date(a.date) as any) - (new Date(b.date) as any));
    // A18 (brief 65) — src/lib/stageOrder.
    const stgs = sortStagesByStart(cs.stages);
    const curStg = stgs[stgs.length - 1] || null;
    return (
      <>
        <button className="btn-back" onClick={() => sDid(null)}>
          {t("case.back_all_cases")}
        </button>
        {suspendDlg && (
          <LinkSuspendDialog name={suspendDlg.name} t={tD} onClose={closeSuspend} onConfirm={confirmSuspend} />
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            margin: "4px 0 12px",
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ fontSize: FS.s15 }}>
            {cs.ref} — {cs.title}
          </h3>
          {cs.ctype && (
            <span
              className="tag"
              style={ctc(cs.ctype, _caseTypeEntries) || undefined}
            >
              {resolveTaxonomyLabel(cs.ctype, _caseTypeEntries)}
            </span>
          )}
          {/* 109 §5 — the STATUS pill beside its date. The bare date lines
              below predate 108 and only became visible when §8e made the dates
              real; a date with no status reads as noise. Colours and shape are
              the matter list's own status pill verbatim
              (matterListCfg.tsx:488 — `.status .status-<key>`), no palette
              invented. Active keeps the clean header: no pill.

              140 §1.3 [RULED, Laurent, 2026-08-27] — AND A PROSPECT EARNS THAT
              SLOT, on the same recipe: "should also show on top of CV and CCV
              as the current stage, otherwise once in the case one could forget
              that it is not a real case yet." So while the matter is tagged
              the header shows the PROSPECT pill in place of the status pill —
              replacing it, exactly as the matter list's Status cell does, and
              never beside it.

              109 §5's rule survives untouched: an ordinary active case still
              has a clean header, because the tag is tested first and only a
              tagged matter takes the slot early. The companion date lines
              below are 109 §5's and are deliberately not touched — a prospect
              is created `active`, so none of them has anything to say about
              one. */}
          {cs.isProspect ? (
            <span className="status status-prospect">
              {t("matters.prospect.badge", { fallback: "Prospect" })}
            </span>
          ) : cs.status && cs.status !== "active" ? (
            <span
              className={`status ${({ pending: "status-pending", closed: "status-closed", archived: "status-archived" } as any)[cs.status] || "status-active"}`}
            >
              {resolveTaxonomyLabel(cs.status, _caseStatusEntries) || cs.status}
            </span>
          ) : null}
          {cs.status === "pending" && cs.pendingDate && (
            <span style={{ fontSize: FS.s10, color: "var(--am)" }}>
              {t("case.status_since", { fallback: "since" })} {fd(cs.pendingDate)}
            </span>
          )}
          {(cs.status === "closed" || cs.status === "archived") &&
            cs.closedDate && (
              <span style={{ fontSize: FS.s10, color: "var(--rd)" }}>
                {t("case.status_closed_on", { fallback: "closed" })}{" "}
                {fd(cs.closedDate)}
              </span>
            )}
          {cs.status === "archived" && cs.archivedDate && (
            <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
              {t("archive.archived_on", { fallback: "archived" })}{" "}
              {fd(cs.archivedDate)}
            </span>
          )}
          {curStg && (
            <span
              style={{
                fontSize: FS.s10,
                background: "var(--as)",
                color: "var(--ac)",
                padding: "2px 8px",
                borderRadius: RAD.r10,
                marginLeft: 2,
              }}
            >
              {t("case.current_stage", { fallback: "Current stage" })}:{" "}
              {resolveTaxonomyLabel(curStg.type, _caseStageEntries)}
              <TaxonomyOrphanMark
                value={curStg.type}
                entries={_caseStageEntries}
                t={t}
              />
            </span>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
            {(canModifyCase ||
              (cs.responsiblePartners || []).includes(currentUser)) && (
              <button
                className="btn btn-ghost"
                style={{ padding: "3px 7px" }}
                onClick={() => om("ca", cs)}
              >
                <Pencil size={I.s} />
              </button>
            )}
            {cs.status === "archived" && (
              <button
                className="btn btn-danger"
                style={{ padding: "3px 7px" }}
                onClick={() =>
                  confirmAndUndo({
                    label: `case ${cs.ref}`,
                    onConfirm: () => del(SK.ca, ca, sCa, cs.id),
                    undo: null,
                  })
                }
              >
                <Trash2 size={I.s} />
              </button>
            )}
          </div>
        </div>
        <div
          className="csGrid"
          style={(() => {
            const wide = [];
            const narrow = [];
            if (canViewDesc || canViewParties)
              wide.push(
                '"' +
                  (canViewDesc ? "info" : ".") +
                  " " +
                  (canViewParties ? "rightStack" : ".") +
                  '"',
              );
            if (canViewAgenda || canViewTasks)
              wide.push(
                '"' +
                  (canViewAgenda ? "agenda" : ".") +
                  " " +
                  (canViewTasks ? "tasks" : ".") +
                  '"',
              );
            if (canViewDocs || canViewTimeline)
              wide.push('"docsAndStages docsAndStages"');
            if (canViewParties) wide.push('"emails emails"');
            // 108 §4 — immediately after the Emails card, so it sits beside
            // time entries. Same gate: it is the same correspondence.
            if (canViewParties) wide.push('"whatsapp whatsapp"');
            if (isPartnerStrict || _isRPHere)
              wide.push('"collabRates collabRates"');
            if (canViewCalculator) wide.push('"calculator calculator"');
            if (canFin) wide.push('"fin fin"');
            if (canViewTime) wide.push('"time time"');
            if (canViewDesc) narrow.push('"info"');
            if (canViewParties) narrow.push('"rightStack"');
            if (canViewAgenda) narrow.push('"agenda"');
            if (canViewTasks) narrow.push('"tasks"');
            if (canViewDocs || canViewTimeline) narrow.push('"docsAndStages"');
            if (canViewParties) narrow.push('"emails"');
            if (canViewParties) narrow.push('"whatsapp"');
            if (isPartnerStrict || _isRPHere) narrow.push('"collabRates"');
            if (canViewCalculator) narrow.push('"calculator"');
            if (canFin) narrow.push('"fin"');
            if (canViewTime) narrow.push('"time"');
            return {
              gridTemplateAreas: wide.join(" "),
              "--narrowAreas": narrow.join(" "),
            };
          })()}
        >
          {canViewDesc && (
            <div
              className="csInfo"
              style={{
                flex: "1 1 400px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <CaseDescriptionCard
                cs={cs}
                showDesc={showDesc}
                setShowDesc={sShowDesc}
                sCol={sCol}
                tog={tog}
                t={t}
              />
              {/* BRIEF 86 PHASE 2 STEP 3 — one component, in src/components. */}
              <CaseInfoSection
                kind="case"
                cs={cs}
                co={co}
                currentUser={currentUser}
                ag={ag}
                ju={ju}
                canFin={canFin}
                canTerms={canTerms}
                isRp={_isRPHere}
                _caseStatusEntries={_caseStatusEntries}
                onCallColleague={onCallColleague}
                callActive={callActive}
                openAgendaModal={om}
                nav={nav}
                sCol={sCol}
                tog={tog}
                t={t}
                te={te}
                inv={inv}
              />
            </div>
          )}
          <div
            className="csRightStack"
            style={{
              gridArea: "rightStack",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              minWidth: 0,
            }}
          >
            {canViewParties && (
              <div
                className="csParties"
                style={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div className="detail-card" style={{ flex: 1, overflow: "visible" }}>
                  <div
                    className="csSecHdr"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: FS.s10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: ".5px",
                        color: "var(--tm)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        // Phase 25.1 r3#2: left zone grows so the action buttons
                        // sit centered between two equal flex-1 sides.
                        flex: "1 1 0",
                      }}
                      onClick={() => {
                        // Phase 25.1 MF5: opening Parties also auto-expands its
                        // three subcategories (Clients / Opponents / Third
                        // parties).
                        const opening = sCol("parties");
                        tog("parties");
                        if (opening)
                          setPartiesOpen((p: any) => ({
                            ...p,
                            [`${cs.id}:g1`]: true,
                            [`${cs.id}:g2`]: true,
                            [`${cs.id}:g3`]: true,
                          }));
                      }}
                    >
                      {!sCol("parties") ? (
                        <ChevronDown size={11} style={{ color: "var(--tm)" }} />
                      ) : (
                        <ChevronRight
                          size={11}
                          style={{ color: "var(--tm)" }}
                        />
                      )}
                      <span>{t("case.parties")}</span>
                    </div>
                    {/* Phase 25.1 r3#2: three action buttons centered in the
                        header (between the flex-1 title and the flex-1 lock
                        slot); fixed regardless of lock visibility. */}
                    <div style={{ display: "flex", gap: 3, flex: "0 0 auto" }}>
                      <button
                        className="btn"
                        style={{
                          padding: "3px 7px",
                          background: "rgba(74,124,255,.15)",
                          color: "var(--ac)",
                          border: "1px solid rgba(74,124,255,.3)",
                        }}
                        title={t("modal.meeting.title")}
                        onClick={(e) => {
                          e.stopPropagation();
                          const td = new Date();
                          const todS2 = td.toISOString().slice(0, 10);
                          const nowH = String(td.getHours()).padStart(2, "0");
                          const nowM = String(
                            (Math.ceil(td.getMinutes() / 15) * 15) % 60,
                          ).padStart(2, "0");
                          const endH = String(
                            td.getHours() +
                              (Math.ceil(td.getMinutes() / 15) * 15 >= 60
                                ? 2
                                : 1),
                          ).padStart(2, "0");
                          const caseCols2 = (cs.caseCollaborators || [])
                            .map((x: any) => co.find((m: any) => m.id === x.coId))
                            .filter(Boolean);
                          /* 119 §5 — THE CAST, NOT THE GROUPING. These five
                             entries stop being the roster's sections and become
                             the POPULATION handed to `meetingRoster`, which
                             groups it into the three §5 names.
                             122 §1 — AND `label` IS LIVE AGAIN, for the people
                             §5 files under (c): a person with no collaborator
                             id keeps the family named here instead of being
                             flattened to "Third parties". `precheck` remains
                             inert (the check rule is `meetingRoster`'s) and is
                             left standing rather than pruned, rule 2.
                             `isCollaborator` is what stops a Client id being
                             read as a Collaborator id. */
                          const grps = [
                            {
                              label: "Internal Team",
                              persons: caseCols2,
                              precheck: true,
                              isCollaborator: true,
                            },
                            {
                              /* 123 §4 — the same derivation App's and CCV's
                                 builders ask. This one is always a case, so it
                                 always asks with `false` and the word does not
                                 change; what changes is that the three cannot
                                 answer differently again. */
                              label: t(clientsFamilyKey(false)),
                              persons: [...cls, ...cps],
                              precheck: false,
                            },
                            {
                              label: t("case.parties.opponents"),
                              persons: [...ops, ...ocs],
                              precheck: false,
                            },
                            {
                              label: t("case.parties.opp_counsel"),
                              persons: ols,
                              precheck: false,
                            },
                            {
                              label: t("case.parties.third_parties"),
                              persons: [...exs, ...mes, ...ars, ...op2s],
                              precheck: false,
                            },
                          ];
                          /* 118 §6 — the flatten is `meetingAttendees` now;
                             the GROUPS stay here, where the id families are.
                             BOTH dialogs on this header take it — the meeting
                             and the availability poll built the identical list
                             twelve lines apart. */
                          /* 119 §5 — grouped by the person's relation to the
                             MATTER, not by which table they came from. Opened
                             from this pane there is no event yet, so `invited`
                             is empty and the result is exactly the old
                             precheck: team ticked, parties not. */
                          const attendees = meetingRoster(
                            grps,
                            pname,
                            teamIdSet((cs.caseCollaborators || []).map((x: any) => x.coId)),
                            new Set<string>(),
                            (k) => t(PEOPLE_GROUP_KEY[k]),
                          );
                          /* BRIEF 120 §3 — THE PARTIES PANE OPENS THE EVENT
                             MODAL. Same gesture, same seed, one surface: the
                             Schedule Meeting dialog is gone and this is the
                             canonical `ag` modal, pre-set for a meeting.
                             `type: "REU"` IS the In Person / Virtual toggle
                             (EV_MEETING_KIND, App.tsx:504@50fb417c) — §3 forbids a
                             second switch that means the same thing.
                             123 §3 — AND THAT IS WITHDRAWN. The type was the
                             wrong switch: it says what kind of event, not how
                             it is held. This seeds the firm's OWN first meeting
                             type (`firstMeetingTypeCode`, never the literal
                             "REU" — tenant 4 has renamed it) plus
                             `meetingMode: "person"`, which is what this button
                             has always opened on.
                             `_caseFixed` keeps the matter un-pickable, which is
                             what the dialog gave for free by having no case
                             field at all. The roster travels as `_attendees`
                             because THIS pane knows its own id sources; App
                             adopts a caller's roster and never re-derives one. */
                          om("ag", {
                            ...(_meetingType ? { type: _meetingType } : {}),
                            meetingMode: "person",
                            caId: cs.id,
                            _caseFixed: true,
                            _attendees: attendees,
                            title: cs.title + " — " + cs.ref,
                            date: todS2,
                            time: nowH + ":" + nowM,
                            endTime: endH + ":" + nowM,
                            location: mtgDefLoc,
                            notes: "",
                          });
                        }}
                      >
                        <Users size={16} color="var(--tp)" />
                      </button>
                      <button
                        className="btn"
                        style={{
                          padding: "3px 7px",
                          background: "rgba(167,139,250,.15)",
                          color: "var(--pp)",
                          border: "1px solid rgba(167,139,250,.3)",
                        }}
                        title={t("modal.poll.title")}
                        onClick={(e) => {
                          e.stopPropagation();
                          const caseCols2 = (cs.caseCollaborators || [])
                            .map((x: any) => co.find((m: any) => m.id === x.coId))
                            .filter(Boolean);
                          /* 119 §5 — THE CAST, NOT THE GROUPING. These five
                             entries stop being the roster's sections and become
                             the POPULATION handed to `meetingRoster`, which
                             groups it into the three §5 names.
                             122 §1 — AND `label` IS LIVE AGAIN, for the people
                             §5 files under (c): a person with no collaborator
                             id keeps the family named here instead of being
                             flattened to "Third parties". `precheck` remains
                             inert (the check rule is `meetingRoster`'s) and is
                             left standing rather than pruned, rule 2.
                             `isCollaborator` is what stops a Client id being
                             read as a Collaborator id. */
                          const grps = [
                            {
                              label: "Internal Team",
                              persons: caseCols2,
                              precheck: true,
                              isCollaborator: true,
                            },
                            {
                              /* 123 §4 — the same derivation App's and CCV's
                                 builders ask. This one is always a case, so it
                                 always asks with `false` and the word does not
                                 change; what changes is that the three cannot
                                 answer differently again. */
                              label: t(clientsFamilyKey(false)),
                              persons: [...cls, ...cps],
                              precheck: false,
                            },
                            {
                              label: t("case.parties.opponents"),
                              persons: [...ops, ...ocs],
                              precheck: false,
                            },
                            {
                              label: t("case.parties.opp_counsel"),
                              persons: ols,
                              precheck: false,
                            },
                            {
                              label: t("case.parties.third_parties"),
                              persons: [...exs, ...mes, ...ars, ...op2s],
                              precheck: false,
                            },
                          ];
                          /* 118 §6 — the flatten is `meetingAttendees` now;
                             the GROUPS stay here, where the id families are.
                             BOTH dialogs on this header take it — the meeting
                             and the availability poll built the identical list
                             twelve lines apart. */
                          /* 119 §5 — grouped by the person's relation to the
                             MATTER, not by which table they came from. Opened
                             from this pane there is no event yet, so `invited`
                             is empty and the result is exactly the old
                             precheck: team ticked, parties not. */
                          const attendees = meetingRoster(
                            grps,
                            pname,
                            teamIdSet((cs.caseCollaborators || []).map((x: any) => x.coId)),
                            new Set<string>(),
                            (k) => t(PEOPLE_GROUP_KEY[k]),
                          );
                          setDoodleDlg({
                            caseObj: cs,
                            attendees,
                            title: cs.title + " — " + cs.ref,
                          });
                        }}
                      >
                        <CalendarClock size={16} color="var(--tp)" />
                      </button>
                      <button
                        className="btn"
                        style={{
                          padding: "3px 7px",
                          background: "rgba(74,222,128,.15)",
                          color: "var(--gn)",
                          border: "1px solid rgba(74,222,128,.3)",
                        }}
                        title={t("caseEmails.send_email_to_all_parties", {
                          fallback: "Send email to all parties",
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          const pool: any[] = [];
                          const push = (p: any) => {
                            if (p && p.email)
                              pool.push({
                                id: p.id,
                                name: pname(p),
                                email: p.email,
                              });
                          };
                          cls.forEach(push);
                          cps.forEach(push);
                          ops.forEach(push);
                          ocs.forEach(push);
                          ols.forEach(push);
                          exs.forEach(push);
                          mes.forEach(push);
                          ars.forEach(push);
                          op2s.forEach(push);
                          if (!pool.length) {
                            toast?.(
                              t("caseEmails.no_party_email", {
                                fallback: "No party with an email on this case",
                              }),
                            );
                            return;
                          }
                          const ccCols2 = (cs.caseCollaborators || [])
                            .map((x: any) => co.find((m: any) => m.id === x.coId))
                            .filter(
                              (c: any) => c && c.id !== currentUser && c.email,
                            );
                          setMailDlg({
                            label: t("caseEmails.case_parties", {
                              fallback: "Case parties",
                            }),
                            persons: pool.map((p) => ({
                              id: p.id,
                              name: p.name,
                              email: p.email,
                              checked: false,
                            })),
                            caseObj: cs,
                            ccCols: ccCols2,
                          });
                        }}
                      >
                        <Mail size={16} color="var(--tp)" />
                      </button>
                    </div>
                    {/* Phase 25.1 r3#2: lock pinned far-right (same place as every
                        other section's lock). Slot reserved via visibility so the
                        centered buttons never shift when the lock hides/shows. */}
                    <div
                      style={{
                        flex: "1 1 0",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          visibility: sCol("parties") ? "hidden" : "visible",
                        }}
                      >
                        <LockButton {...({} as any)}
                          locked={partiesLocked}
                          onToggle={() => setPartiesLocked((v: any) => !v)}
                        />
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const poKey = (k: any) => `${cs.id}:${k}`;
                    const poGet = (k: any, dflt: any) => {
                      const v = partiesOpen[poKey(k)];
                      return v === undefined ? dflt : v;
                    };
                    const poSet = (k: any, dflt: any) =>
                      setPartiesOpen((p: any) => {
                        const cur =
                          p[poKey(k)] === undefined ? dflt : p[poKey(k)];
                        return { ...p, [poKey(k)]: !cur };
                      });
                    const cnt1 =
                      (cs.clIds || []).length + (cs.cpIds || []).length;
                    const cnt2 =
                      (cs.opIds || []).length +
                      (cs.ocIds || []).length +
                      (cs.ocuIds || []).length;
                    const cnt3 =
                      (cs.exIds || []).length +
                      (cs.meIds || []).length +
                      (cs.arIds || []).length +
                      (cs.op2Ids || []).length;
                    const g1Open = poGet("g1", cnt1 > 0);
                    const g2Open = poGet("g2", cnt2 > 0);
                    const g3Open = poGet("g3", cnt3 > 0);
                    const subCnts: Record<string, any> = {
                      clIds: (cs.clIds || []).length,
                      cpIds: (cs.cpIds || []).length,
                      opIds: (cs.opIds || []).length,
                      ocIds: (cs.ocIds || []).length,
                      ocuIds: (cs.ocuIds || []).length,
                      exIds: (cs.exIds || []).length,
                      meIds: (cs.meIds || []).length,
                      arIds: (cs.arIds || []).length,
                      op2Ids: (cs.op2Ids || []).length,
                    };
                    const allKeys = [
                      "g1",
                      "g2",
                      "g3",
                      "clIds",
                      "cpIds",
                      "opIds",
                      "ocIds",
                      "ocuIds",
                      "exIds",
                      "meIds",
                      "arIds",
                      "op2Ids",
                    ];
                    const anyOpen = allKeys.some((k) => {
                      const dflt =
                        k === "g1"
                          ? cnt1 > 0
                          : k === "g2"
                            ? cnt2 > 0
                            : k === "g3"
                              ? cnt3 > 0
                              : subCnts[k] > 0;
                      return poGet(k, dflt);
                    });
                    const expandAll = () => {
                      const upd: Record<string, any> = {};
                      allKeys.forEach((k) => (upd[poKey(k)] = true));
                      setPartiesOpen((p: any) => ({ ...p, ...upd }));
                    };
                    const collapseAll = () => {
                      const upd: Record<string, any> = {};
                      allKeys.forEach((k) => (upd[poKey(k)] = false));
                      setPartiesOpen((p: any) => ({ ...p, ...upd }));
                    };
                    // A8 (brief 65) — src/lib/mailDialogSeed. This copy has no
                    // empty guard and keeps not having one.
                    const openMailDlg = (label: any, persons: any, caseObj: any) =>
                      setMailDlg(
                        mailDialogSeed(label, persons, caseObj, {
                          pname,
                          collaborators: co,
                          currentUser,
                        }),
                      );
                    const GH = (
                      gkey: any,
                      dflt: any,
                      label: any,
                      total: any,
                      hasBorder: any,
                      mailPersons: any,
                    ) => (
                      <div
                        onClick={() => poSet(gkey, dflt)}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "4px 0",
                          userSelect: "none",
                          ...(hasBorder
                            ? {
                                borderTop: "1px solid var(--br)",
                                marginTop: 8,
                                paddingTop: 8,
                              }
                            : {}),
                        }}
                      >
                        <span
                          style={{
                            fontSize: FS.s8,
                            color: "var(--tm)",
                            width: 10,
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          {poGet(gkey, dflt) ? "▼" : "▶"}
                        </span>
                        <span
                          style={{
                            fontSize: FS.s10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: ".5px",
                            color: "var(--tp)",
                            flex: 1,
                          }}
                        >
                          {label}
                        </span>
                        {mailPersons &&
                          (() => {
                            const hasEmails = mailPersons.some(
                              (p: any) => p && p.email,
                            );
                            return (
                              <button
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: hasEmails ? "pointer" : "default",
                                  padding: "0 2px",
                                  opacity: hasEmails ? 1 : 0.25,
                                  color: "var(--tp)",
                                  flexShrink: 0,
                                }}
                                title={
                                  hasEmails
                                    ? t("case.send_email_to", { label })
                                    : t("case.no_email_addresses")
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (hasEmails)
                                    openMailDlg(label, mailPersons, cs);
                                }}
                              >
                                <Mail size={21} color="var(--tp)" />
                              </button>
                            );
                          })()}
                      </div>
                    );
                    const compactGH = (label: any, hasBorder: any, mailPersons: any) => {
                      const hasEmails = mailPersons.some((p: any) => p && p.email);
                      return (
                        <div
                          key={label}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "4px 0",
                            userSelect: "none",
                            ...(hasBorder
                              ? {
                                  borderTop: "1px solid var(--br)",
                                  marginTop: 8,
                                  paddingTop: 8,
                                }
                              : {}),
                          }}
                        >
                          <span style={{ width: 10, flexShrink: 0 }} />
                          <span
                            style={{
                              fontSize: FS.s10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: ".5px",
                              color: "var(--tp)",
                              flex: 1,
                            }}
                          >
                            {label}
                          </span>
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              cursor: hasEmails ? "pointer" : "default",
                              padding: "0 2px",
                              opacity: hasEmails ? 1 : 0.25,
                              color: "var(--tp)",
                              flexShrink: 0,
                            }}
                            title={
                              hasEmails
                                ? t("case.send_email_to", { label })
                                : t("case.no_email_addresses")
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hasEmails)
                                openMailDlg(label, mailPersons, cs);
                            }}
                          >
                            <Mail size={21} color="var(--tp)" />
                          </button>
                        </div>
                      );
                    };
                    if (sCol("parties"))
                      return (
                        <>
                          {compactGH(t("case.parties.clients"), false, [
                            ...cls,
                            ...cps,
                          ])}
                          {compactGH(t("case.parties.opponents"), true, [
                            ...ops,
                            ...ocs,
                            ...ols,
                          ])}
                          {compactGH(t("case.parties.third_parties"), true, [
                            ...exs,
                            ...mes,
                            ...ars,
                            ...op2s,
                          ])}
                        </>
                      );
                    return (
                      <>
                        {GH(
                          "g1",
                          cnt1 > 0,
                          t("case.parties.clients"),
                          cnt1,
                          false,
                          [...cls, ...cps],
                        )}
                        {g1Open && (
                          <>
                            <MultiSelect
                              colKey="clIds"
                              isOpen={poGet("clIds", subCnts.clIds > 0)}
                              onToggle={() => poSet("clIds", subCnts.clIds > 0)}
                              l={t("case.parties.clients")}
                              opts={cl}
                              sel={cs.clIds || []}
                              onChange={(v: any, rm: any) =>
                                updCsMerge(
                                  rm ? { clIds: v, clRoles: rm } : { clIds: v },
                                )
                              }
                              roleEntries={partyRoleEntries}
                              roleMap={cs.clRoles || {}}
                              junctionType="client"
                              /* 141 §2.2 — the ONE family with a per-matter
                                 lifecycle. Locked = the inert display arm, as
                                 on the CCV roster: a status write is a data
                                 modification and is unlock-only. */
                              chipExtra={(clientId: any) => {
                                const st = clientLinkStatus(clientId);
                                return (
                                  <StatusPill
                                    cfg={linkStatusCfg(t, st)}
                                    current={st || undefined}
                                    title={
                                      partiesLocked
                                        ? undefined
                                        : t("cc.tip.click_change_status", {
                                            fallback: "Click to change status",
                                          })
                                    }
                                    options={
                                      partiesLocked
                                        ? undefined
                                        : clientStatusOptions
                                    }
                                    onPick={
                                      partiesLocked
                                        ? undefined
                                        : (code: string) =>
                                            setClientStatus(clientId, code)
                                    }
                                    style={{
                                      border: "none",
                                      fontWeight: 600,
                                      fontFamily: "inherit",
                                    }}
                                  />
                                );
                              }}
                              onRemove={(id: any, n: any) => rmWithUndo("clIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "cl",
                                  retKey: "clIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            <MultiSelect
                              colKey="cpIds"
                              titleField="function"
                              isOpen={poGet("cpIds", subCnts.cpIds > 0)}
                              onToggle={() => poSet("cpIds", subCnts.cpIds > 0)}
                              l={t("case.parties.client_contacts")}
                              opts={cp}
                              sel={cpSel}
                              chipMuted={cpMuted}
                              chipExtra={cpChipExtra}
                              onChange={(v: any) => updCsField("cpIds", v)}
                              onRemove={(id: any, n: any) => rmWithUndo("cpIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "cp",
                                  retKey: "cpIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            {cls.length > 0 &&
                              cps.length > 0 &&
                              (<ClientContactsSublist cls={cls} cps={cps} t={t} pname={pname} links={csL.cpLinks} roleLabel={lawyerWord} />)}
                          </>
                        )}
                        {GH(
                          "g2",
                          cnt2 > 0,
                          t("case.parties.opponents"),
                          cnt2,
                          true,
                          [...ops, ...ocs, ...ols],
                        )}
                        {g2Open && (
                          <>
                            <MultiSelect
                              colKey="opIds"
                              isOpen={poGet("opIds", subCnts.opIds > 0)}
                              onToggle={() => poSet("opIds", subCnts.opIds > 0)}
                              l={t("case.parties.opponents")}
                              opts={op}
                              sel={cs.opIds || []}
                              onChange={(v: any, rm: any) =>
                                updCsMerge(
                                  rm ? { opIds: v, opRoles: rm } : { opIds: v },
                                )
                              }
                              roleEntries={partyRoleEntries}
                              roleMap={cs.opRoles || {}}
                              junctionType="opponent"
                              onRemove={(id: any, n: any) => rmWithUndo("opIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "op",
                                  retKey: "opIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            <MultiSelect
                              colKey="ocIds"
                              titleField="function"
                              isOpen={poGet("ocIds", subCnts.ocIds > 0)}
                              onToggle={() => poSet("ocIds", subCnts.ocIds > 0)}
                              l={t("case.parties.opp_contacts")}
                              opts={oc}
                              sel={cs.ocIds || []}
                              onChange={(v: any) => updCsField("ocIds", v)}
                              onRemove={(id: any, n: any) => rmWithUndo("ocIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "oc",
                                  retKey: "ocIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            {ops.length > 0 &&
                              ocs.length > 0 &&
                              (<OpponentContactsSublist ops={ops} ocs={ocs} t={t} pname={pname} />)}
                            <MultiSelect
                              colKey="ocuIds"
                              isOpen={poGet("ocuIds", subCnts.ocuIds > 0)}
                              onToggle={() =>
                                poSet("ocuIds", subCnts.ocuIds > 0)
                              }
                              l={t("case.parties.opp_counsel")}
                              opts={ocu}
                              sel={ocuSel}
                              chipMuted={ocuMuted}
                              chipExtra={ocuChipExtra}
                              onChange={(v: any, rm: any) =>
                                updCsMerge(
                                  rm
                                    ? { ocuIds: v, ocuRoles: rm }
                                    : { ocuIds: v },
                                )
                              }
                              roleEntries={partyRoleEntries}
                              roleMap={cs.ocuRoles || {}}
                              junctionType="opponentLawyer"
                              onRemove={(id: any, n: any) => rmWithUndo("ocuIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "ocu",
                                  retKey: "ocuIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            {opsPool.length > 0 &&
                              olsPool.length > 0 &&
                              (<OpponentCounselSublist ops={opsPool as readonly { id: number }[]} ols={olsPool as readonly { id: number }[]} links={csL.ocuLinks} t={tD} pname={pname} />)}
                          </>
                        )}
                        {GH(
                          "g3",
                          cnt3 > 0,
                          t("case.parties.third_parties"),
                          cnt3,
                          true,
                          [...exs, ...mes, ...ars, ...op2s],
                        )}
                        {g3Open && (
                          <>
                            <MultiSelect
                              colKey="exIds"
                              isOpen={poGet("exIds", subCnts.exIds > 0)}
                              onToggle={() => poSet("exIds", subCnts.exIds > 0)}
                              l={t("nav.experts")}
                              opts={ex}
                              sel={cs.exIds || []}
                              onChange={(v: any) => updCsField("exIds", v)}
                              onRemove={(id: any, n: any) => rmWithUndo("exIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "ex",
                                  retKey: "exIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            <MultiSelect
                              colKey="meIds"
                              isOpen={poGet("meIds", subCnts.meIds > 0)}
                              onToggle={() => poSet("meIds", subCnts.meIds > 0)}
                              l={t("case.parties.mediators")}
                              opts={me}
                              sel={cs.meIds || []}
                              onChange={(v: any) => updCsField("meIds", v)}
                              onRemove={(id: any, n: any) => rmWithUndo("meIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "me",
                                  retKey: "meIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            <MultiSelect
                              colKey="arIds"
                              isOpen={poGet("arIds", subCnts.arIds > 0)}
                              onToggle={() => poSet("arIds", subCnts.arIds > 0)}
                              l={t("case.parties.arbiters")}
                              opts={ar}
                              sel={cs.arIds || []}
                              onChange={(v: any) => updCsField("arIds", v)}
                              onRemove={(id: any, n: any) => rmWithUndo("arIds", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "ar",
                                  retKey: "arIds",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                            <MultiSelect
                              colKey="op2Ids"
                              isOpen={poGet("op2Ids", subCnts.op2Ids > 0)}
                              onToggle={() =>
                                poSet("op2Ids", subCnts.op2Ids > 0)
                              }
                              l={t("case.parties.other_persons")}
                              opts={op2}
                              sel={cs.op2Ids || []}
                              onChange={(v: any, rm: any) =>
                                updCsMerge(
                                  rm
                                    ? { op2Ids: v, op2Roles: rm }
                                    : { op2Ids: v },
                                )
                              }
                              roleEntries={partyRoleEntries}
                              roleMap={cs.op2Roles || {}}
                              junctionType="additionalOpponent"
                              onRemove={(id: any, n: any) => rmWithUndo("op2Ids", id, n)}
                              onNew={() => {
                                sQm({
                                  type: "op2",
                                  retKey: "op2Ids",
                                  caseId: cs.id,
                                });
                                sQf({});
                              }}
                              locked={partiesLocked}
                              onViewItem={__viewParty}
                            />
                          </>
                        )}
                        {undoEntry && undoEntry.caseId === cs.id && (
                          <div
                            key={undoEntry.id + undoEntry.field}
                            style={{
                              position: "relative",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginTop: 10,
                              padding: "7px 10px 10px",
                              background: "var(--rd)",
                              borderRadius: RAD.r8,
                              fontSize: FS.s11,
                            }}
                          >
                            <span
                              style={{
                                flex: 1,
                                color: CT.c_fff,
                                fontSize: FS.s10_5,
                                fontWeight: 500,
                              }}
                            >
                              {t("case.undo.removed", {
                                name: undoEntry.name,
                                fallback: `Removed ${undoEntry.name}`,
                              })}
                            </span>
                            <button
                              type="button"
                              style={{
                                padding: "2px 10px",
                                fontSize: FS.s10,
                                background: "none",
                                border: "1.5px solid rgba(255,255,255,.8)",
                                color: CT.c_fff,
                                borderRadius: RAD.r6,
                                cursor: "pointer",
                                fontWeight: 600,
                                flexShrink: 0,
                                fontFamily: "inherit",
                              }}
                              onClick={doUndo}
                            >
                              {t("common.undo", { fallback: "Undo" })}
                            </button>
                            <div
                              style={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                height: 3,
                                background: "rgba(255,255,255,.45)",
                                animation: `ud ${String(UNDO_WINDOW_MS)}ms linear forwards`,
                              }}
                            />
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            {canViewCdd && (
              <div
                className="csSec-cdd"
                style={{ minWidth: 0 }}
                data-secollapsed={sCol("cdd") ? "1" : "0"}
              >
                <SectionHeader {...({} as any)}
                  title={t("case.section.cdd", {
                    fallback: "Client and case due diligence",
                  })}
                  collapsible
                  open={!sCol("cdd")}
                  onToggle={() => tog("cdd")}
                />
                <CddCaseSection
                  caseRow={cs}
                  co={co}
                  currentUserId={currentUser}
                  nav={nav}
                />
              </div>
            )}
            {/* Phase 36 (A/E) — the per-case CONFID folder was eliminated
                (provisioning consolidated to the per-client folder). The
                folder link now lives per linked client in the CDD section. */}
          </div>
          {canViewAgenda && (
            <CaseAgendaSection
              kind="case"
              covered={caseBento}
              cs={cs}
              cag={cag}
              caseStageEntries={_caseStageEntries}
              co={co}
              currentUser={currentUser}
              ag={ag}
              sAg={sAg}
              s={s}
              SK={SK}
              agRef={agRef}
              coAvs={coAvs}
              evtTypes={evtTypes}
              etTag={etTag}
              evtLongestCode={evtLongestCode}
              locIcon={locIcon}
              MultiSelect={MultiSelect}
              _ITEM_LEVEL={_ITEM_LEVEL}
              itemLevelList={itemLevelList}
              evExpanded={evExpanded}
              setEvExpanded={setEvExpanded} // 110 §1h
              toggleEvExpanded={toggleEvExpanded}
              agMy={agMy}
              setAgMy={sAgMy}
              agWeek={agWeek}
              setAgWeek={setAgWeek}
              agPage={agPage}
              setAgPage={setAgPage}
              agLocked={agLocked}
              setAgLocked={setAgLocked}
              canEditAgenda={canEditAgenda}
              canEditOthersTasks={canEditOthersTasks}
              /* brief 91 §3a — the event drawer's timer column. */
              activeTimers={activeTimers}
              startTimer={startTimer}
              confirmAndUndo={confirmAndUndo}
              openModal={om}
              sCol={sCol}
              tog={tog}
              t={t}
            />
          )}
          {canViewTasks && (
            /* BRIEF 86 PHASE 2 STEP 4 — one component, in src/components.
               A BLOCK comment, not a JSX one: this sits inside {cond && (…)},
               which takes exactly one child. */
            <CaseTasksSection
              kind="case"
              userMatrixQuadrant={userMatrixQuadrant}
              cs={cs} co={co} tk={tk} sTk={sTk} s={s} SK={SK}
              ca={ca} cc={[]} currentUser={currentUser} tkTypes={tkTypes}
              itemLevelList={itemLevelList} itemLevelBase={itemLevelBase}
              itemLevelOrder={itemLevelOrder} _ITEM_LEVEL={_ITEM_LEVEL}
              tkExpanded={tkExpanded} setTkExpanded={setTkExpanded}
              tkLocked={tkLocked} setTkLocked={setTkLocked}
              tkMatrix={tkMatrix} setTkMatrix={setTkMatrix}
              bentoOpen={caseBento} setBentoOpen={setCaseBento}
              tkMy={tkMy} setTkMy={setTkMy}
              tkPage={tkPage} setTkPage={setTkPage}
              tkSort={tkSort} setTkSort={setTkSort}
              tkStatusFilt={tkStatusFilt} setTkStatusFilt={setTkStatusFilt}
              tkRef={tkRef}
              canEditTasks={canEditTasks} canEditOthersTasks={canEditOthersTasks}
              _isRPHere={_isRPHere} _permViewer={_permViewer}
              activeTimers={activeTimers} startTimer={startTimer}
              confirmAndUndo={confirmAndUndo} svTkData={svTkData}
              _navCase={_navCase} nav={nav}
              onSubtaskDone={onSubtaskDone}
              openTaskModal={om}
              sCol={sCol} tog={tog} t={t} toast={toast}
              TkStatusTag={TkStatusTag}
            />
          )}
          {/* AUTHORITY (155 §4): none — this docs+stages composition and
              CCV's are deliberate parallels mounting the same shared sections
              with kind="case"/"cc"; a layout change lands in BOTH. */}
          {(canViewDocs || canViewTimeline) && (
            <div className="csSec-docsAndStages">
              {canViewDocs && (
                <div
                  className="csSec-gendocs"
                  style={{ flex: "1 1 400px", minWidth: 0 }}
                >
                  <CaseDocumentsSection
                    key={cs.id}
                    kind="case"
                    cs={cs}
                    cl={cl}
                    op={op}
                    co={co}
                    currentUser={currentUser}
                    linkTypesList={linkTypesList}
                    stageTypesList={stageTypesList}
                    toast={toast}
                    docLocked={docLocked}
                    setDocLocked={setDocLocked}
                    canViewDocs={canViewDocs}
                    canManageDocs={canManageDocs}
                    canLinkDocs={canLinkDocs}
                    isAdmin={!!_permViewer?.isAdmin}
                    isRPHere={_isRPHere}
                    assigned={_assignedHere}
                    titleKey="case.documents"
                    paginate={true}
                    // 140 THE RECONCILE — read for ONE field
                    // (caseFolderNameTemplate), so the rename offer can name
                    // the folder it is proposing.
                    firmSettings={firmSettings}
                    onProvisionFolders={onProvisionFolders}
                    collapsible
                    sectionOpen={!sCol("gendocs")}
                    onSectionToggle={() => tog("gendocs")}
                    onUpdateCase={(patch: any) =>
                      s(
                        SK.ca,
                        ca.map((c: any) =>
                          c.id === cs.id ? { ...c, ...patch } : c,
                        ),
                        sCa,
                      )
                    }
                    onDeleteLink={(lk: any) =>
                      confirmAndUndo({
                        label: "this link",
                        toastName: `Removed "${lk.name}"`,
                        onConfirm: () =>
                          s(
                            SK.ca,
                            ca.map((c: any) =>
                              c.id === cs.id
                                ? {
                                    ...c,
                                    links: (c.links || []).filter(
                                      (x: any) => x.id !== lk.id,
                                    ),
                                  }
                                : c,
                            ),
                            sCa,
                          ),
                        undo: {
                          snapshot: lk,
                          restore: (snap) =>
                            s(
                              SK.ca,
                              caRef.current.map((c: any) =>
                                c.id === cs.id
                                  ? { ...c, links: [...(c.links || []), snap] }
                                  : c,
                              ),
                              sCa,
                            ),
                        },
                      })
                    }
                  />
                </div>
              )}
              {canViewTimeline && (
                <div className="csStagesCol">
                  <div
                    className="csSec-stages"
                    style={{ minWidth: 0 }}
                    data-secollapsed={sCol("stages") ? "1" : "0"}
                  >
                    <div
                      className="csSecHdr"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        margin: "10px 0 6px",
                        flexWrap: "wrap",
                      }}
                      id="ca-stages"
                    >
                      <button
                        type="button"
                        aria-expanded={!sCol("stages")}
                        title={
                          !sCol("stages")
                            ? t("common.collapse", { fallback: "Collapse" })
                            : t("common.expand", { fallback: "Expand" })
                        }
                        onClick={() => tog("stages")}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                          color: "var(--tm)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 14,
                          height: 14,
                          flexShrink: 0,
                        }}
                      >
                        {!sCol("stages") ? (
                          <ChevronDown size={11} />
                        ) : (
                          <ChevronRight size={11} />
                        )}
                      </button>
                      <h3
                        style={{ fontSize: FS.s12, margin: 0, cursor: "pointer" }}
                        onClick={() => tog("stages")}
                      >
                        {t("case.stages")}
                      </h3>
                      {!sCol("stages") && (
                        <div
                          style={{
                            marginLeft: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          {!stgLocked && (
                            <AddButton {...({} as any)}
                              label={t("common.add_button.add", {
                                fallback: "Add",
                              })}
                              onClick={() =>
                                om("stg", {
                                  _ci: cs.id,
                                  registerNumber: getLatestRegNum(cs),
                                })
                              }
                            />
                          )}
                          <LockButton {...({} as any)}
                            locked={stgLocked}
                            onToggle={() => setStgLocked((v) => !v)}
                          />
                        </div>
                      )}
                    </div>
                    {(() => {
                      /* 151 §4 — THE ALL-DOCUMENTS PANE IS RETIRED
                         [RULED, point (vii)]. The §3 stage-subdrawer row
                         (CaseStageTimeline) is the one classification
                         surface now — shared with CCV, which closes the
                         CV/CCV feature gap. Unstaged rows were COUNTED
                         FIRST (staging: 0, all tenants, 2026-08-30) and
                         the 20260830130000_151 migration deletes them;
                         the one modal's required Link to Stage is what
                         keeps the class extinct. paneDocs, docRow and
                         its om("doc") pencil — DocumentModal's only
                         opener (132 §2) — died with the pane. */
                      /* 186 (e) [RULED 186 (16)] — the shared search field,
                         and nothing else: the list the pager below pages is
                         the filtered one; `allStages` (the modal's select)
                         stays the full list. The field renders under the
                         header like the list does — the spine's CSS hides
                         both while the section is closed. `stgs` is read once
                         into a typed row list (the eslint ratchet). */
                      const stgRows = stgs as StageRow[];
                      const stgsShown = stgQ.trim()
                        ? stgRows.filter((st) => stageMatchesQuery(st, stgQ, { caseStageEntries: _caseStageEntries, partyRoleEntries, ju }))
                        : stgRows;
                      return (
                        <>
                          <SectionSearch value={stgQ} onChange={setStgQ} />
                          {stgs.length === 0 && (
                            <p
                              style={{
                                fontSize: FS.s11,
                                color: "var(--tm)",
                                marginBottom: 8,
                              }}
                            >
                              {t("case.empty.no_stages")}
                            </p>
                          )}
                          {stgsShown.length > 0 &&
                            (() => {
                              /* 186 close (6) — the THIRD copy of the pager
                                 (B.7a's brief counted two) pages through the
                                 shared mechanism: the same ten, the same
                                 arithmetic, the same control. */
                              const { cur: __cur, tot: __tot, pageItems: __sliced } = pageOf(stgsShown, stgPager.page);
                              return (
                                <>
                                  <CaseStageTimeline
                                    /* 110 §6a — remount per matter: clears the
                                       timeline's own notesOpen (stage ids
                                       collide across matters; see the stgExp
                                       clear in the §5 effect). */
                                    key={"stgtl-" + cs.id}
                                    matterRow={cs} // 111 §5c
                                    stages={__sliced}
                                    docs={cs.docs || []}
                                    ju={ju}
                                    _caseStageEntries={_caseStageEntries}
                                    partyRoleEntries={partyRoleEntries}
                                    events={cag}
                                    stagesOpen={stagesOpen}
                                    stgExp={stgExp}
                                    setStgExp={setStgExp}
                                    stgLocked={stgLocked}
                                    /* 151 §2 step 3 — the modal's stage
                                       select reads the FULL list; `stages`
                                       above is this page of the pager. */
                                    allStages={stgs}
                                    docTypesList={_docTypes}
                                    docStatusesList={_docStatuses}
                                    /* 109 §7b — the stage drawer's add writes
                                       through THIS view's own s(SK.ca) docs
                                       path, stageId stamped. 151 §4 deleted
                                       the all-documents pane and
                                       DocumentModal; this drawer is the one
                                       entry point. */
                                    onCreateStageDoc={(stageId, doc) =>
                                      s(
                                        SK.ca,
                                        ca.map((c: any) =>
                                          c.id === cs.id
                                            ? {
                                                ...c,
                                                docs: [
                                                  ...(c.docs || []),
                                                  {
                                                    id: nid(c.docs || []),
                                                    name: doc.name || doc.link,
                                                    link: doc.link,
                                                    /* 151 §2 (iii) — the
                                                       modal infers the date
                                                       (casefile: last-
                                                       modified; weblink:
                                                       recorded-on); this
                                                       write stores what it
                                                       was handed, and an
                                                       empty date stays empty
                                                       — never a false today. */
                                                    date: doc.date || "",
                                                    fileType: doc.fileType,
                                                    /* 151 §2 step 3 — (iv)'s
                                                       completed fields; the
                                                       legacy defaults stand
                                                       in where the user left
                                                       one empty, byte-equal
                                                       to what the retired
                                                       DocumentModal wrote. */
                                                    type: doc.type || "other",
                                                    status: doc.status || "",
                                                    note: doc.note || "",
                                                    stageId,
                                                  },
                                                ],
                                              }
                                            : c,
                                        ),
                                        sCa,
                                      )
                                    }
                                    /* 151 §3 (x) — the pencil's write: the
                                       same s(SK.ca) docs path, correcting in
                                       place; stageId is the draft's (the
                                       Link to Stage select is the
                                       move-to-stage now). */
                                    onUpdateStageDoc={(docId, doc) =>
                                      s(
                                        SK.ca,
                                        ca.map((c: any) =>
                                          c.id === cs.id
                                            ? {
                                                ...c,
                                                docs: (c.docs || []).map(
                                                  (dd: any) =>
                                                    dd.id === docId
                                                      ? {
                                                          ...dd,
                                                          name:
                                                            doc.name ||
                                                            doc.link,
                                                          link: doc.link,
                                                          date: doc.date || "",
                                                          fileType:
                                                            doc.fileType,
                                                          type:
                                                            doc.type ||
                                                            "other",
                                                          status:
                                                            doc.status || "",
                                                          note: doc.note || "",
                                                          stageId:
                                                            doc.stageId != null
                                                              ? Number(
                                                                  doc.stageId,
                                                                )
                                                              : null,
                                                        }
                                                      : dd,
                                                ),
                                              }
                                            : c,
                                        ),
                                        sCa,
                                      )
                                    }
                                    /* 151 §3 (x) — the bin, in THIS view's
                                       own delete shape (confirm-and-undo,
                                       the doomed pane's own). */
                                    onDeleteStageDoc={(d: any) =>
                                      confirmAndUndo({
                                        label: "this document",
                                        toastName: `Removed "${d.name}"`,
                                        onConfirm: () =>
                                          s(
                                            SK.ca,
                                            ca.map((c: any) =>
                                              c.id === cs.id
                                                ? {
                                                    ...c,
                                                    docs: (
                                                      c.docs || []
                                                    ).filter(
                                                      (dd: any) =>
                                                        dd.id !== d.id,
                                                    ),
                                                  }
                                                : c,
                                            ),
                                            sCa,
                                          ),
                                        undo: {
                                          snapshot: d,
                                          restore: (snap: any) =>
                                            s(
                                              SK.ca,
                                              caRef.current.map((c: any) =>
                                                c.id === cs.id
                                                  ? {
                                                      ...c,
                                                      docs: [
                                                        ...(c.docs || []),
                                                        snap,
                                                      ],
                                                    }
                                                  : c,
                                              ),
                                              sCa,
                                            ),
                                        },
                                      })
                                    }
                                    onSaveStageNotes={(stageId, notes) =>
                                      s(
                                        SK.ca,
                                        ca.map((c: any) =>
                                          c.id === cs.id
                                            ? {
                                                ...c,
                                                stages: (c.stages || []).map(
                                                  (x: any) =>
                                                    x.id === stageId
                                                      ? { ...x, notes: notes || "" }
                                                      : x,
                                                ),
                                              }
                                            : c,
                                        ),
                                        sCa,
                                      )
                                    }
                                    oneDrive={{ caseId: cs.id }}
                                    onEditStage={(st) =>
                                      om("stg", {
                                        _ci: cs.id,
                                        _si: st.id,
                                        ...st,
                                        juId: st.juId || "",
                                      })
                                    }
                                    onDeleteStage={(st) =>
                                      confirmAndUndo({
                                        label: "this stage",
                                        toastName: `Removed "${st.type || "stage"}"`,
                                        onConfirm: () =>
                                          s(
                                            SK.ca,
                                            ca.map((c: any) =>
                                              c.id === cs.id
                                                ? {
                                                    ...c,
                                                    stages: (
                                                      c.stages || []
                                                    ).filter(
                                                      (x: any) => x.id !== st.id,
                                                    ),
                                                  }
                                                : c,
                                            ),
                                            sCa,
                                          ),
                                        undo: {
                                          snapshot: st,
                                          restore: (snap) =>
                                            s(
                                              SK.ca,
                                              caRef.current.map((c: any) =>
                                                c.id === cs.id
                                                  ? {
                                                      ...c,
                                                      stages: [
                                                        ...(c.stages || []),
                                                        snap,
                                                      ],
                                                    }
                                                  : c,
                                              ),
                                              sCa,
                                            ),
                                        },
                                      })
                                    }
                                    t={t}
                                  />
                                  {__tot > 1 && <Pager cur={__cur} tot={__tot} setPage={stgPager.setPage} />}
                                </>
                              );
                            })()}
                        </>
                      );
                    })()}
                  </div>
                  <ListLoadContext.Provider value={_chronLists}>
                    <CaseTimelineSection {...({} as any)}
                      kind="case"
                      cs={cs}
                      ag={ag}
                      te={te}
                      co={co}
                      chatMsgs={chatMsgs}
                      chronicle={chronicle}
                      currentUser={currentUser}
                      canViewTimeline={canViewTimeline}
                      sectionOpen={!sCol("timeline")}
                      onSectionToggle={() => tog("timeline")}
                    />
                  </ListLoadContext.Provider>
                  {/* 182 A.3 — the contact trail beside the audit trail: the Notes
                      section (Interaction) directly after the timeline, rendered
                      only for a holder of caseView.notes.view. */}
                  {canViewNotes && (
                    <InteractionSection
                      anchorId="ca-notes"
                      caseId={cs.id}
                      viewerId={currentUser?.id}
                      isAdmin={_permViewer?.isAdmin === true}
                      canCreate={canCreateNotes}
                      canEdit={canEditNotes}
                      sectionOpen={!sCol("notes")}
                      onSectionToggle={() => tog("notes")}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {(isPartnerStrict || _isRPHere) && (
          <CaseTeamSection
            kind="case"
            cs={cs}
            co={co}
            tk={tk}
            te={te}
            OPEN_ST={OPEN_ST}
            canTerms={canTerms}
            canAssignCollabs={canAssignCollabs}
            isPartnerStrict={isPartnerStrict}
            _isRPHere={_isRPHere}
            _permViewer={_permViewer}
            finCalc={finCalc}
            teamOpen={teamOpen}
            setTeamOpen={setTeamOpen}
            teamViewLocked={teamViewLocked}
            setTeamViewLocked={setTeamViewLocked}
            collabLocked={collabLocked}
            setCollabLocked={setCollabLocked}
            addCoOpen={addCoOpen}
            setAddCoOpen={setAddCoOpen}
            crPage={crPage}
            setCrPage={setCrPage}
            onCollaboratorsChange={(next) =>
              s(
                SK.ca,
                ca.map((x: any) =>
                  x.id === cs.id ? { ...x, caseCollaborators: next } : x,
                ),
                sCa,
              )
            }
            onCollaboratorsRestore={(fn) =>
              s(
                SK.ca,
                caRef.current.map((x: any) =>
                  x.id === cs.id
                    ? { ...x, caseCollaborators: fn(x.caseCollaborators || []) }
                    : x,
                ),
                sCa,
              )
            }
            confirmAndUndo={confirmAndUndo}
            t={t}
          />
          )}
          {canViewCalculator && (
            <div
              className="csSec-calculator"
              style={{ flex: "1 1 100%", minWidth: 0, order: 50.5 }}
              data-secollapsed={sCol("calculator") ? "1" : "0"}
            >
              <SectionHeader {...({} as any)}
                title={t("case.calculator.title", {
                  fallback: "Interest calculator",
                })}
                collapsible
                open={!sCol("calculator")}
                onToggle={() => tog("calculator")}
              />
              <CaseCalculatorSection
                kind="case"
                cs={cs}
                canEdit={canEditCalculator}
                toast={toast}
                hideTitle={true}
              />
            </div>
          )}
          <div
            className="csSec-fin"
            style={{
              flex: "1 1 400px",
              minWidth: 0,
              order: 51,
              display: canFin ? undefined : "none",
            }}
            data-secollapsed={sCol("fin") ? "1" : "0"}
          >
            <SectionHeader {...({} as any)}
              title={t("case.financial_summary")}
              collapsible
              open={!sCol("fin")}
              onToggle={() => tog("fin")}
            />
            {(() => {
              if (!canFin) return null;
              const isAdmin = canRecordPayments;
              // C.2 — the table's money is server-handed (finCalc block for
              // this case); the view renders what it was given ("—" until the
              // first compute lands).
              const fin = finCalc?.[cs.id]?.block || null;
              // 161 §8 [R6/R10] — the recap shows the Invoices strip's items,
              // in the strip's order, ten rows, one basis per row on both
              // surfaces. The old aug/billed rows are gone from the RENDER;
              // the block still serves them for their other readers.
              const recap = fin ? fin.recap : null;
              const isPartnerOnly = canTerms;
              const paid = recap ? recap.paid : null;
              const due = recap ? recap.due : null;
              const staffCost = recap ? recap.staffCost : null;
              const overheads = recap ? recap.overheads : null;
              const grossMargin = recap ? recap.grossMargin : null;
              const marginPct = recap ? recap.marginPct : null;
              const marginPctColor =
                marginPct == null
                  ? "var(--tm)"
                  : marginPct > 30
                    ? "var(--gn)"
                    : marginPct >= 10
                      ? "var(--am)"
                      : "var(--rd)";
              /* 199 §F — the two lines' figures, read off the served block
                 through named shapes (F.3: the server's costsLine and activity —
                 the builders Frais et honoraires reads — nothing computed here).
                 The activity rows: the `activity_code` taxonomy in its order
                 (`tkTypes`, App's own list) for the codes THIS matter carries,
                 plus any code the rows carry that the taxonomy no longer names
                 (the union rule), each with §C.0's three figures. */
              interface FinCostsLine {
                costsRatio?: number | null; totalRate?: number | null; pctByPartner?: number | null;
                avgRatePerH?: number | null; effectiveRatePerH?: number | null; totalMin?: number | null;
                totalInvoiced?: number | null; totalPaidExVat?: number | null; totalDueExVat?: number | null;
                effectiveMargin?: number | null; discardedPayments?: number; discardedGross?: number;
                discardedForeign?: Record<string, number> | null; discardedDueDocs?: number; discardedDueGross?: number;
              }
              interface FinActivityCell { min?: number | null; cost?: number | null; pct?: number | null; wc?: number | null /* 214 addendum 1 §13 */ }
              interface FinRecapSeam { marginBase?: number | null; marginPct?: number | null /* 214 addendum 1 §11 */ }
              interface FinActivity { byType?: Record<string, FinActivityCell | undefined> | null }
              /* 181's rule for a file whose props arrive as `any`: the translator
                 narrowed ONCE at this seam, so the rows below are typed calls. */
              const tt: Translate = t;
              const __finB = fin as { costsLine?: FinCostsLine | null; activity?: FinActivity | null; recap?: FinRecapSeam | null } | null;
              const __cl = __finB?.costsLine ?? null;
              // 214 addendum 1 §11 [RULED 57] — the margin's base, served beside the margin (recapBlock's `marginBase`),
              // and the margin's percentage in the reader's number format (« 90,2 % » in fr).
              const marginBase = __finB?.recap?.marginBase ?? null;
              const marginPctText = pct1(__finB?.recap?.marginPct ?? null, __locale);
              const __clDiscarded = (() => {
                const count = __cl?.discardedPayments ?? 0;
                if (count <= 0) return undefined;
                const parts: string[] = [];
                if ((__cl?.discardedGross ?? 0) > 0) parts.push(fc(__cl?.discardedGross ?? 0));
                for (const [code, v] of Object.entries(__cl?.discardedForeign ?? {})) parts.push(`${String(Math.floor(v))} ${code}`);
                const gross = parts.length ? parts.join(" + ") : fc(0);
                return tt("costs.col.effective_rate_h_discarded", { count, gross, fallback: `${String(count)} payment(s) discarded — no pro forma with a VAT split behind them — ${gross} gross; their hours still count` });
              })();
              const __clDueDiscarded = (() => {
                const count = __cl?.discardedDueDocs ?? 0;
                if (count <= 0) return undefined;
                const gross = fc(__cl?.discardedDueGross ?? 0);
                return tt("costs.col.total_due_ex_vat_discarded", { count, gross, fallback: `${String(count)} sent document(s) discarded — no VAT split stored — ${gross} still owed on them, gross` });
              })();
              const __byType = __finB?.activity?.byType ?? {};
              const __actRows = (() => {
                const out: { code: string; label: string; min: number | null; cost: number | null; pct: number | null; wc: number | null }[] = [];
                const seen = new Set<string>();
                const push = (code: string, label: string) => {
                  const cell = __byType[code];
                  if (!cell || seen.has(code)) return;
                  seen.add(code);
                  // 214 addendum 1 §13 — `wc`, the activity's fees + costs ex VAT, served on the block.
                  out.push({ code, label, min: cell.min ?? null, cost: cell.cost ?? null, pct: cell.pct ?? null, wc: cell.wc ?? null });
                };
                for (const ty of ((tkTypes as { code?: string; desc?: string }[] | null | undefined) ?? [])) {
                  if (ty.code) push(ty.code, ty.desc ?? ty.code);
                }
                for (const code of Object.keys(__byType)) push(code, code);
                return out;
              })();
              // Brief 86 §2b — RP hours / collaborator hours / total hours,
              // per row, computed HERE and not in the server block.
              //
              // WHY NOT THE SERVER. server/src/lib/caseFinancials.js is a
              // RECORDED compute: recordings/c2-financials/financials_recording
              // .json is its oracle and two verifiers reproduce it bit-exact.
              // Adding members to caseBlock() would put a recording regen in
              // front of three columns of hours. It buys nothing: minutes need
              // no rate resolution, `min` is not in TIME_ENTRY_FINANCIAL_FIELDS
              // so it is never scrubbed, and `cte` here is the identical
              // predicate the server applies to the identical posted rows
              // (CaseView.tsx:998 vs caseFinancials.js:98) — so totalHours
              // equals the block's own totM by construction.
              type HrRow = {
                coId?: number | null; min?: number | null; billed?: boolean;
                mark?: string | null; invoiceId?: number | null;
                type?: string | null; // 214 addendum 1 §13 — the activity code, for the partner / collaborators split per activity
              };
              const _rpSet = new Set(cs.responsiblePartners || []);
              const _hrs = (rows: HrRow[]) => {
                const rp = rows.reduce(
                  (s2: number, e: HrRow) => s2 + (_rpSet.has(e.coId) ? e.min || 0 : 0),
                  0,
                );
                const all = rows.reduce((s2: number, e: HrRow) => s2 + (e.min || 0), 0);
                return { rp, col: all - rp, tot: all };
              };
              // 214 addendum 1 §13 — the matter's rows, narrowed ONCE (181's rule) for the per-activity split below.
              const __cte: HrRow[] = cte as HrRow[];
              // 161 §8 — the five hour rows take the SAME predicates as the
              // server's recap (mark + invoiceId split; `sent` = bound to a
              // document with either send stamp), extended from 86 §2b's
              // billed/unbilled pair.
              const _sentInv = new Set(
                (inv || [])
                  .filter((i: any) => i.caseId === cs.id && !i.deletedAt
                    && i.paymentStatus !== "cancelled" && (i.sentToClientAt || i.sentToPeppolAt))
                  .map((i: any) => i.id),
              );
              const hUnm = _hrs(cte.filter((e: HrRow) => e.invoiceId == null && e.mark !== "billable" && e.mark !== "unbillable"));
              const hUnb = _hrs(cte.filter((e: HrRow) => e.invoiceId == null && e.mark === "unbillable"));
              const hBil = _hrs(cte.filter((e: HrRow) => e.invoiceId == null && e.mark === "billable"));
              const hInv = _hrs(cte.filter((e: HrRow) => e.invoiceId != null));
              const hSent = _hrs(cte.filter((e: HrRow) => e.invoiceId != null && _sentInv.has(e.invoiceId)));
              /* 158 §4 [RULED] — THE SECOND WAY TO RECORD A PAYMENT IS GONE.
                 What stood here was the older entry: an inline form in the
                 financial recap with its own amount/date/notes fields, its own
                 pro-forma picker (brief 87 §1c) and `svPy`, whose two arms
                 wrote either POST /:id/payments or a bare `Case.payments` row
                 with no allocation behind it at all.

                 A payment is now recorded in ONE place — the button beside the
                 pro forma, through the modal 157 §1 made safe. Removed with
                 it: the `PayablePF` type, `payableInv` and `svPy`; the bound
                 arm was only a second CALL SITE of `recordInvoicePayment`,
                 which the pro-forma modal and InvoiceTable still own.

                 THE LIST BELOW STAYS. `Case.payments` rows exist in prod, the
                 recap's Payé/Solde dû rows still sum them
                 (server/src/lib/caseFinancials.js:165), and hiding money the
                 row above still counts would be worse than showing it. It can
                 no longer grow — only be read and removed. */
              // A1 (brief 65) — TH/MC/ML now live in src/lib/moneyTableCells.
              return (
                <div>
                  {currentUser && !isAdmin && (
                    <div
                      style={{
                        marginBottom: 6,
                        fontSize: FS.s9_5,
                        color: "var(--tm)",
                      }}
                    >
                      {t("case.fin.payments_visible_partners")}
                    </div>
                  )}
                  <div
                    className="table-wrap"
                    style={{ marginBottom: isAdmin ? 10 : 0 }}
                    data-fin-table="recap"
                  >
                    <Table style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          {TH("")}
                          {/* Brief 86 §2b — the three hour columns, BOTH views,
                              before the fees columns. `fin.col.*` and not
                              `case.fin.col.*`: the same three words appear in
                              CollectiveCaseView's table too, and brief 59's
                              finding was one widget carrying five keys and five
                              wordings. A namespace belonging to neither view is
                              what stops that. The tables themselves stay two
                              implementations, per §0. */}
                          {/* BRIEF 90 §2b — "Responsible Partner hours" in full: its two
                              neighbours already spell themselves out, so this
                              was the only initialism in the row. A NEW KEY —
                              `fin.col.rp_hours` is seeded on every box and the
                              seeder is insert-if-missing, so editing its text
                              corrects no database (82g, then 86d). */}
                          {TH(
                            t("fin.col.rp_hours_full", {
                              fallback: "Responsible Partner hours",
                            }),
                            1,
                          )}
                          {TH(
                            t("fin.col.collab_hours", {
                              fallback: "Collaborator hours",
                            }),
                            1,
                          )}
                          {TH(
                            t("fin.col.total_hours", {
                              fallback: "Total hours",
                            }),
                            1,
                          )}
                          {/* Brief 87 §5 — the adaptive gap. `grow` puts
                              width:100% on this one cell, so the leftover space
                              opens between the HOURS group and the FEES group
                              instead of after RP hours. The two tables stay two
                              implementations (§0), so the same one-flag change
                              lands twice, deliberately. */}
                          {TH(t("case.fin.col.fees_only"), 1, 1)}
                          {TH(t("case.fin.col.fees_costs"), 1)}
                          {TH(t("case.fin.col.fees_costs_vat"), 1)}
                        </tr>
                      </thead>
                      <tbody>
                        {/* 161 §8 [R6] — TEN ROWS, the strip's items in the
                            strip's order, hours for the first five ("—"
                            elsewhere), the colour rule per row: unmarked
                            green, unbillable red, billable amber, invoiced
                            accent, sent muted, paid green, due red/green,
                            costs red, margin green/red. Money placement keeps
                            the table's convention: entry/document rows fill
                            the trio; paid/due are incl-VAT figures (right
                            column); staff costs, overheads and margin are
                            ex-VAT figures (middle column). */}
                        <tr style={{ background: "rgba(52,211,153,.04)" }}>
                          {ML(t("case.fin.row.unmarked", { fallback: "Unmarked" }), "var(--gn)")}
                          {HC(hUnm.rp, "var(--gn)")}
                          {HC(hUnm.col, "var(--gn)")}
                          {HC(hUnm.tot, "var(--gn)")}
                          {MC(recap?.unmarked?.fo, "var(--gn)")}
                          {MC(recap?.unmarked?.wc, "var(--gn)")}
                          {MC(recap?.unmarked?.vt, "var(--gn)")}
                        </tr>
                        <tr style={{ background: "rgba(248,113,113,.04)" }}>
                          {ML(t("case.fin.row.unbillable", { fallback: "Unbillable" }), "var(--rd)")}
                          {HC(hUnb.rp, "var(--rd)")}
                          {HC(hUnb.col, "var(--rd)")}
                          {HC(hUnb.tot, "var(--rd)")}
                          {MC(recap?.unbillable?.fo, "var(--rd)")}
                          {MC(recap?.unbillable?.wc, "var(--rd)")}
                          {MC(recap?.unbillable?.vt, "var(--rd)")}
                        </tr>
                        <tr style={{ background: "rgba(251,191,36,.05)" }}>
                          {ML(t("case.fin.row.billable", { fallback: "Billable" }), "var(--am)")}
                          {HC(hBil.rp, "var(--am)")}
                          {HC(hBil.col, "var(--am)")}
                          {HC(hBil.tot, "var(--am)")}
                          {MC(recap?.billable?.fo, "var(--am)")}
                          {MC(recap?.billable?.wc, "var(--am)")}
                          {MC(recap?.billable?.vt, "var(--am)")}
                        </tr>
                        <tr style={{ borderTop: "1px solid var(--br)" }}>
                          {ML(t("case.fin.row.invoiced", { fallback: "Invoiced" }), "var(--ac)")}
                          {HC(hInv.rp, "var(--ac)")}
                          {HC(hInv.col, "var(--ac)")}
                          {HC(hInv.tot, "var(--ac)")}
                          {MC(recap?.invoiced?.fo, "var(--ac)", undefined, foreignOf(recap, "invoiced", "fo"))}
                          {MC(recap?.invoiced?.wc, "var(--ac)", undefined, foreignOf(recap, "invoiced", "wc"))}
                          {MC(recap?.invoiced?.vt, "var(--ac)", undefined, foreignOf(recap, "invoiced", "vt"))}
                        </tr>
                        <tr>
                          {ML(t("case.fin.row.sent", { fallback: "Sent" }), "var(--tm)")}
                          {HC(hSent.rp, "var(--tm)")}
                          {HC(hSent.col, "var(--tm)")}
                          {HC(hSent.tot, "var(--tm)")}
                          {MC(recap?.sent?.fo, "var(--tm)", undefined, foreignOf(recap, "sent", "fo"))}
                          {MC(recap?.sent?.wc, "var(--tm)", undefined, foreignOf(recap, "sent", "wc"))}
                          {MC(recap?.sent?.vt, "var(--tm)", undefined, foreignOf(recap, "sent", "vt"))}
                        </tr>
                        {isAdmin && (
                          <>
                            {/* 214 addendum 1 §10 [RULED 55] — « Payé » and « Dû »: the five
                                cells that can hold no figure are EMPTY (`EC`), where `HC(null)`
                                / `MC(null)` printed « — » — « columns where no figure is
                                supposed to appear should not show a line like it's missing
                                something » (Laurent, 03/10). A dash stays only where a figure
                                belongs and cannot be given: the summary not served yet, a
                                computed 0 €/h (198 A.3a). */}
                            <tr style={{ background: "rgba(52,211,153,.05)" }} data-fin-row="paid">
                              {ML(t("case.fin.paid"), "var(--gn)")}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {MC(paid, "var(--gn)", undefined, foreignOf(recap, "paid"))}
                            </tr>
                            <tr
                              style={{
                                background:
                                  (due ?? 0) > 0
                                    ? "rgba(248,113,113,.06)"
                                    : "rgba(52,211,153,.06)",
                              }}
                              data-fin-row="due"
                            >
                              {ML(
                                t("case.fin.row.due", { fallback: "Due" }),
                                (due ?? 0) > 0 ? "var(--rd)" : "var(--gn)",
                                600,
                              )}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {EC()}
                              {MC(due, (due ?? 0) > 0 ? "var(--rd)" : "var(--gn)", 600, foreignOf(recap, "due"))}
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  {/* 214 addendum 1 §11 [RULED 55, 57] — THE PART OF ITS OWN. The table above
                      keeps the strip's seven lines under its hour and money columns; every
                      other line stood under « Honoraires + frais », which none of them is
                      (161 §8 [R6] placed the costs and the margin there, 199 §F.2 the rest).
                      Below the table, under the partner gate as before, each line is its
                      label and its figure on one right edge — no column headers, so no empty
                      cell [ADVISOR]. Three groups: « Coûts et marge » (staff costs ·
                      overheads · the margin's BASE, served as `marginBase` and printed just
                      above the margin [RULED 57] · the gross margin, its percentage in the
                      reader's number format — `pct1`), « Frais et honoraires » (199 §F's
                      members, « Total facturé » gone: it is the table's « Facturé », §12),
                      and « Par activité » (§13 — a small table of its own). Colours and tips
                      as before, line for line. The figures stay the SERVER's (199 §F.3). */}
                  {isPartnerOnly && (
                    <div data-fin-part style={{ marginBottom: 10 }}>
                      <div className="table-wrap">
                        <Table style={{ width: "100%" }}>
                          <tbody>
                            {SH(tt("fin.part.costs_margin", { fallback: "Costs and margin" }), 2)}
                            <tr style={{ background: "rgba(248,113,113,.04)" }} data-fin-line="staff_cost">
                              {ML(tt("case.fin.staff_cost", { fallback: "Staff costs" }), "var(--rd)")}
                              {MC(staffCost, "var(--rd)")}
                            </tr>
                            <tr style={{ background: "rgba(248,113,113,.04)" }} data-fin-line="overheads">
                              {ML(tt("case.fin.overheads", { fallback: "Overheads" }), "var(--rd)")}
                              {MC(overheads, "var(--rd)")}
                            </tr>
                            <tr data-fin-line="margin_base">
                              {ML(tt("fin.row.margin_base", { fallback: "Invoiced (pro formas, after discount)" }), "var(--ac)")}
                              {MC(marginBase, "var(--ac)")}
                            </tr>
                            <tr
                              style={{
                                background:
                                  (grossMargin ?? 0) >= 0
                                    ? "rgba(52,211,153,.06)"
                                    : "rgba(248,113,113,.06)",
                              }}
                              data-fin-line="gross_margin"
                            >
                              {ML(
                                <span>
                                  {tt("case.fin.gross_margin")}
                                  {marginPctText != null && (
                                    <span
                                      data-fin-margin-pct
                                      style={{
                                        fontSize: FS.s9_5,
                                        marginLeft: 6,
                                        color: marginPctColor,
                                        fontWeight: 600,
                                        fontFamily:
                                          "'JetBrains Mono',monospace",
                                      }}
                                    >
                                      {marginPctText}
                                    </span>
                                  )}
                                </span>,
                                (grossMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)",
                                600,
                              )}
                              {MC(
                                grossMargin,
                                (grossMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)",
                                600,
                              )}
                            </tr>
                            {SH(tt("nav.costs"), 2)}
                            <tr data-fin-line="pct_costs">
                              {ML(tt("costs.col.pct_costs", { fallback: "% costs" }), "var(--tm)")}
                              {TC(pctCo(__cl?.costsRatio))}
                            </tr>
                            <tr data-fin-line="total_rate">
                              {ML(tt("costs.col.total_rate", { fallback: "Total rate" }), "var(--tm)")}
                              {TC(ratCo(__cl?.totalRate), undefined, undefined, tt("costs.col.total_rate_title", { fallback: "Reference rate × (1 + % costs / 100)" }))}
                            </tr>
                            <tr data-fin-line="pct_by_partner">
                              {ML(tt("costs.col.pct_by_partner"), "var(--tm)")}
                              {TC(pctCo(__cl?.pctByPartner), undefined, undefined, tt("costs.col.pct_by_partner_title", { fallback: "Minutes worked by the fee-earning roles ÷ every minute worked on the matter" }))}
                            </tr>
                            <tr data-fin-line="avg_rate_h">
                              {ML(tt("costs.col.avg_rate_h"), "var(--tm)")}
                              {TC(ratCo(__cl?.avgRatePerH))}
                            </tr>
                            <tr data-fin-line="effective_rate_h">
                              {ML(tt("costs.col.effective_rate_h", { fallback: "Paid/h." }), "var(--tm)")}
                              {TC(ratCo(__cl?.effectiveRatePerH), undefined, undefined, __clDiscarded ?? tt("costs.col.effective_rate_h_title", { fallback: "Payments received ex-VAT ÷ every hour worked, all marks (unmarked, unbillable, billable, invoiced)" }))}
                            </tr>
                            <tr data-fin-line="total_time">
                              {ML(tt("costs.col.total_time"), "var(--tm)")}
                              {HC(__cl?.totalMin ?? null, "var(--tp)")}
                            </tr>
                            <tr data-fin-line="total_paid_ex_vat">
                              {ML(tt("costs.col.total_paid_ex_vat", { fallback: "Paid ex VAT" }), "var(--gn)")}
                              {TC(__cl?.totalPaidExVat != null ? fc(__cl.totalPaidExVat) : null, "var(--gn)", undefined, __clDiscarded)}
                            </tr>
                            <tr data-fin-line="total_due_ex_vat">
                              {ML(tt("costs.col.total_due_ex_vat", { fallback: "Due ex VAT" }), (__cl?.totalDueExVat ?? 0) > 0 ? "var(--rd)" : "var(--gn)")}
                              {TC(__cl?.totalDueExVat != null ? fc(__cl.totalDueExVat) : null, (__cl?.totalDueExVat ?? 0) > 0 ? "var(--rd)" : "var(--gn)", undefined, __clDueDiscarded ?? tt("costs.col.total_due_ex_vat_title", { fallback: "What the sent documents still owe, each at its own VAT split" }))}
                            </tr>
                            <tr data-fin-line="effective_margin">
                              {ML(tt("costs.col.effective_margin", { fallback: "Effective margin" }), (__cl?.effectiveMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)", 600)}
                              {TC(__cl?.effectiveMargin != null ? fc(__cl.effectiveMargin) : null, (__cl?.effectiveMargin ?? 0) >= 0 ? "var(--gn)" : "var(--rd)", 600, tt("costs.col.effective_margin_title", { fallback: "Paid ex VAT − staff costs − overheads" }))}
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                      {/* 214 addendum 1 §13 [RULED 56] — « PAR ACTIVITÉ », A SMALL TABLE OF ITS
                          OWN: per activity, the responsible partner's hours, the collaborators'
                          hours, the total (`_hrs` over the activity's rows, as the table's hour
                          lines — 86 §2b), its fees + costs ex VAT (`wc`, served on the block —
                          the sum of what those entries show in the Amount column, in cents;
                          docs/214-deploy-note.md §7.13) and its internal cost (199 §C.0's
                          figure, in the neutral colour, no longer red under a fees header
                          [ADVISOR]). The label keeps 205 §1's chip, name and share. */}
                      {__actRows.length > 0 && (
                        <div className="table-wrap" style={{ marginTop: 6 }} data-fin-activity-table>
                          <Table style={{ width: "100%" }}>
                            <thead>
                              {SH(tt("costs.section.by_activity", { fallback: "By activity" }), 6)}
                              <tr>
                                {TH("")}
                                {TH(tt("fin.col.rp_hours_full", { fallback: "Responsible Partner hours" }), 1)}
                                {TH(tt("fin.col.collab_hours", { fallback: "Collaborator hours" }), 1)}
                                {TH(tt("fin.col.total_hours", { fallback: "Total hours" }), 1)}
                                {TH(tt("case.fin.col.fees_costs"), 1, 1)}
                                {TH(tt("fin.col.internal_cost", { fallback: "Internal cost" }), 1)}
                              </tr>
                            </thead>
                            <tbody>
                              {__actRows.map((a) => {
                                const h = _hrs(__cte.filter((e) => String(e.type ?? "") === a.code));
                                return (
                                  <tr key={a.code} data-fin-activity={a.code}>
                                    {ML(
                                      <span>
                                        {/* 205 §1 — the one chip (`TYPE_CHIP`), the name, the share (206 §1: `wide`). */}
                                        <span style={{ ...TYPE_CHIP, marginRight: 6 }}>{a.code}</span>
                                        {a.label}
                                        <span data-fin-activity-share style={{ fontSize: FS.s9_5, marginLeft: 6, color: "var(--tm)", fontWeight: 600, fontFamily: "'JetBrains Mono',monospace" }}>
                                          {pctCo(a.pct)}
                                        </span>
                                      </span>,
                                      "var(--ts)",
                                      undefined,
                                      true,
                                    )}
                                    {HC(h.rp, "var(--tp)")}
                                    {HC(h.col, "var(--tp)")}
                                    {HC(h.tot, "var(--tp)")}
                                    {MC(a.wc, "var(--tp)")}
                                    {MC(a.cost, "var(--tp)")}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                  {/* 161 §7 — the 159 PROVISIONS block is RETIRED: a provision
                      is a pro forma of docKind `retainer` now (R1), so money
                      on a bare matter has no store, no surface and no writer.
                      The retainers live in the table below like any document. */}
                  {/* 158 §5 — the SAME table the Invoices section
                      draws, since that is where this component now
                      comes from. `scope` says the host already names
                      the matter, so the CASE and TITLE columns stay
                      folded — the shape this view always had. */}
                  <InvoiceTable
                    invoices={(inv || []).filter((i: any) => i.caseId === cs.id)}
                    scope={{ type: "ca", id: cs.id }}
                    labelled
                    inv={inv}
                    sInv={sInv}
                    toast={toast}
                    onViewInvoice={(id: any) => setPfMod({ invoiceId: id })}
                    /* 179 B.6 — the approval pill's switch. Typed at the seam so the
                       host's `any` does not add an unsafe assignment (the eslint ratchet). */
                  />
                </div>
              );
            })()}
          </div>
          {canViewTime && (
            /* BRIEF 86 PHASE 2 STEP 1 — one component, in src/components,
               beside the 27 both views already share. A comment cannot lead a
               `{cond && (…)}` body without a fragment, so it is a block comment
               inside the parens rather than a JSX one. */
            <CaseTimeEntriesSection
              {...({} as any)}
              kind="case"
              cs={cs}
              co={co}
              currentUser={currentUser}
              /* 198 §C — the SETTINGS OBJECT, not the resolved axis: the period
                 filter's two accounting-year kinds read the firm's start off it
                 (D.3); the work week resolves from it just the same. */
              schedAxis={firmSettings}
              te={te}
              sTe={sTe}
              s={s}
              SK={SK}
              ca={ca}
              cc={[]}
              inv={inv}
              canSeeCounters={canSeeTeCounters}
              tkTypes={tkTypes}
              canTerms={canTerms}
              canEditTime={canEditTime}
              canMarkBilled={canMarkBilled}
              canImportExport={canTeImportExport}
              canPillColumn={canTePillColumn}
              canBulkEdit={canBulkEdit}
              onBulkEdit={(ids: number[], onDone: (r: TeBulkUpdateResult) => void) =>
                om("teBulk", { _teIds: ids, _onDone: onDone })
              }
              canBulkDelete={canBulkDelete}
              confirmAndUndo={confirmAndUndo}
              nid={nid}
              _roleRows={_roleRows}
              openTimeModal={om}
              onDelete={(e: any) =>
                confirmAndUndo({
                  label: "this time entry",
                  toastName: `Removed "${e.desc || "time entry"}"`,
                  onConfirm: () => s(SK.te, te.filter((t: any) => t.id !== e.id), sTe),
                  undo: { snapshot: e, restore: (snap) => s(SK.te, [...teRef.current, snap], sTe) },
                })
              }
              onProForma={(p: any) => setPfMod({ ...(Array.isArray(p) ? { entryIds: p } : p), csId: cs.id, csType: "ca" })}
              sCol={sCol}
              tog={tog}
              t={t}
              toast={toast}
              style={{ order: 99 }}
            />
          )}
          {canViewParties && (
            /* BRIEF 86 PHASE 2 STEP 2 — one component. 194 B.1b: the party
               list is served, so the five id lists and directories this mount
               handed over are gone; 194 B.3: so are the matter-choice lists
               (the drawer asks the matter only when mounted matter-less). */
            <CaseEmailsPane
              kind="case"
              cs={cs}
              currentUser={currentUser}
              co={co}
              toast={toast}
              firmSettings={firmSettings} /* 198 §C — the period pickers' axis input (D.3) */
              openLogTime={(prefill: Record<string, unknown>) => om("te", prefill)}
              sCol={sCol}
              tog={tog}
              t={t}
              className="csSec-emails"
            />
          )}
          {/* 194 B.0 — the WhatsApp card (108 §4) is mounted inside
              CaseEmailsPane now, the second pane of "Communication". */}
          {pfMod && (
            <ProFormaModal {...({} as any)}
              key={pfMod?.invoiceId ? `pf-view-${pfMod.invoiceId}` : "pf-create"}
              pfMod={pfMod}
              invoiceId={pfMod?.invoiceId || null}
              onClose={() => setPfMod(null)}
              onRemake={(p: any) => setPfMod(p)}
              te={te}
              sTe={sTe}
              ca={ca}
              cc={[]}
              cl={cl}
              co={co}
              inv={inv}
              sInv={sInv}
              s={s}
              toast={toast}
              tkTypes={tkTypes}
              tk={tk}
              sTk={sTk}
              currentUser={currentUser}
              firmSettings={firmSettings}
            />
          )}
        </div>
        {!canViewDesc &&
          !canViewParties &&
          !canViewAgenda &&
          !canViewTasks &&
          !canViewDocs &&
          !canViewTimeline &&
          !canFin &&
          !canTerms &&
          !canViewTime && (
            <p
              style={{
                textAlign: "center",
                color: "var(--tm)",
                fontSize: FS.s11_5,
                padding: "16px 12px",
                fontStyle: "italic",
              }}
            >
              {t("case.no_access_message", {
                fallback:
                  "You don't have access to any sections of this case. Contact the case Responsible Partner if you need access.",
              })}
            </p>
          )}
        <ConfirmDeleteDialog dlg={confirmDlg} onCancel={cancelConfirm} />
        <UndoToast toast={undoToast} onUndo={runUndo} />
      </>
    );
  }
  // A20 (brief 65) — src/lib/localToday.
  const todayStrCa = localTodayStr();
  // A4 (brief 65) — the reader lives in src/lib/matterMetrics; the five names
  // that differed between this copy and CollectiveCaseView's are the arguments.
  const caseMetrics = (c: any) =>
    readMatterMetrics(c, {
      fin: finCalc,
      tasks: tk,
      events: ag,
      taskOwnerKey: "caseId",
      eventOwnerKey: "caId",
      rosterField: "clIds",
      todayStr: todayStrCa,
      taskStatusEntries: _taskStatusEntries,
    });
  // A16 (brief 65) — src/lib/nextEventDate; the owner key and the today string
  // are the two things that differed between the views.
  const nextEvDate = (c: any) =>
    nextEventDate(ag, "caId", c.id, todayStrCa);
  // Brief 57 §3c — the list is a CONFIG now (src/lib/matterListCfg.tsx),
  // rendered by EntityList's declared shell. What this view still owns: the
  // entity computations (caseMetrics, nextEvDate, statusDate), the list
  // state, the import modal and its file plumbing, and the dialogs.
  const caListCfg = buildMatterListCfg({
    kind: "ca",
    customFieldCols, // 189 §B
    /* 190 round 5 [R] — the column switchboard's gate: the load window keeps
       every column (viewer null), then `can(furniture.list.<k>)`. */
    colCan: columnGateOf({ viewer: _permViewer, can: _permCan }), // 191 fold-in 2 — the one gate
    t,
    fd,
    fc,
    rows: ca,
    ju,
    co,
    currentUser,
    metrics: caseMetrics,
    nextEvDate,
    statusDate,
    todayStr: todayStrCa,
    sort: caSort,
    setSort: setCaSort,
    chipsValue: cf,
    onChips: sCf,
    myToggle: currentUser
      ? {
          active: caMy,
          onToggle: () => setCaMy((v) => !v),
          count: ca.filter((c: any) =>
            (c.responsiblePartners || []).includes(currentUser),
          ).length,
        }
      : null,
    // 139 §2.3 — the second axis; the builder owns the labels and counts.
    prospectFilter: { value: caProspect, onChange: setCaProspect },
    searchText: caSearchQ,
    onSearch: setCaSearchQ,
    /* 195 §C.3e — `searchExtra: q` (57c-iii's fold-in of the page-level
       text) is gone with the page-level box: MEASURED, that text was always
       "" here — the box never drew on the "ca" section and every navigation
       reset it — so nothing this list matches moves. */
    locked: caListLocked,
    onLockToggle: () => setCaListLocked((v) => !v),
    onImportOpen: () => caFileRef.current?.click(),
    onFolderCheck: onFolderCheck as (() => void) | undefined, // 212 §27 — typed at the seam
    canCreate: _permCan("lateral.cases.create"),
    onAdd: () => om("ca"),
    /* 139 §2.5 — the second create button. `{ isProspect: true }` with no id
       is the arm App's case modal already carries (App.tsx:8075, unit 2's
       comment names this exact call): the modal opens pre-filled from the
       prospect ref series and the create route writes the tag. */
    onAddProspect: () => om("ca", { isProspect: true }),
    caseTypeEntries: _caseTypeEntries,
    caseStatusEntries: _caseStatusEntries,
    caseStageEntries: _caseStageEntries,
    // 58 §5b — THE FIRM'S AXIS, and it was the one §4/§5 prop neither matter
    // list passed. `matterListCfg.tsx:641` calls `axisDayOf(schedAxis, dw)`
    // unguarded, so both lists threw on their first render and showed nothing.
    // The view already receives it from App; only this hop was missing.
    schedAxis,
    // 132 §6 — the same hop the axis needed: the view receives it from App
    // and the strip lives in matterListCfg.
    holidays,
    // 133 §4 — the same hop again: the three calendar-display switches are
    // resolved in App and only the strip reads them here.
    calDisp,
    agTypeFilt,
    setAgTypeFilt,
    // 58 §2 row 15 — the same list-level financial gate the collective list
    // has used since Gate 4 C6 (CollectiveCaseView's `canSeeFin`), so one
    // column declaration answers to one predicate on both.
    canSeeFin: canFin,
    getInvoiceHeaderLabel,
    evtTypes,
    stripEvents: (ds: string) =>
      ag.filter((a: any) => a.date && a.date.slice(0, 10) === ds),
    stripMatterOf: (ev: any) =>
      ev.caId ? ca.find((c: any) => c.id === ev.caId) : null,
    stripClick: (ev: any) => {
      if (ev.caId) {
        // C-1 — scroll queues only on actual nav.
        if (_navCase("ca", ev.caId)) queueScrollToEv(ev.id);
      } else om("ag", ev);
    },
    navAgenda: () => nav("ag"),
    rowClick: (c: any) => sDid(c.id),
  });
  /* 190 §D — THE BOARD, over Bento — round 2 [B], RULED: no more Liste |
     Tableau strip; the list Bento is a capability EntityList carries (`bento`):
     its icon sits in the filter row, and while open the pane (CaseBoardView —
     the picker, the board) renders in place of the list body only, over the
     rows the filters left. A card moves when the viewer holds
     `surface.bento.list` (190 §E — the server enforces it on the write), and
     the move writes ONE BentoPlacement row, firm-wide, and nothing else
     (190 (3)). The open state is App's transient `caMode` (190 round 6 [Z] — it was the `caseListMode` preference). The
     `any`-typed names narrowed at the seam (185's method). */
  const tB = t as TFn;
  const setCaModeB = setCaMode as Dispatch<"list" | "board">;
  const caModeB: "list" | "board" = caMode === "board" ? "board" : "list";
  const boardMovable = _permCan("surface.bento.list");
  const caBento = _permCan("surface.bento.list")
    ? {
        active: caModeB === "board",
        onToggle: () => { setCaModeB(caModeB === "board" ? "list" : "board"); },
        title: tB("bento.list_title", { fallback: "List Bento" }),
        /* 190 [I] — the picker, in the icon's row right of it (EntityList renders it while active). */
        picker: <BentoPicker bento={listBento} id="bento-template-list" />,
        pane: (rows: unknown[], ctx: unknown) => (
          <CaseBoardView
            /* [S](i) — the template spanning both kinds: this list's filtered
               rows, then EVERY (non-archived) collective case behind them —
               the other list's own filters do not reach here — each tagged
               by kind; the board reads the tag, not the neighbours. */
            cases={listBento.allMatters
              ? [...(rows as BoardCase[]), ...(cc as BoardCase[]).map((c) => ({ ...c, kind: "cc" as const }))]
              : (rows as BoardCase[])}
            kind="ca"
            bentoOther={listBento.allMatters ? listBentoOther : null}
            /* [C] — the template's fields resolved to the list's OWN columns
               (label and cell renderer, bound to the list's context), so the
               card shows a field exactly as the list does. */
            fields={listBento.fields.flatMap((k): BoardField[] => {
              const col = (caListCfg.cols.t as BoardColumn[]).find((x) => x.k === k);
              if (!col) return [];
              const label = col.raw === true && typeof col.l === "string" ? col.l : tB(col.lk ?? `list.ca.col.${k}`, { fallback: col.l ?? k });
              return [{ k, label, render: (c) => col.r(c, ctx) }];
            })}
            bento={listBento}
            movable={boardMovable}
            /* 190 round 6 [Z] — a card click LEAVES the Bento (the Eisenhower's
               own onCardClick closes its dialog): the list is the table again
               when "← Tous les dossiers" brings the reader back. */
            onOpen={({ kind, id }) => { setCaModeB("list"); if (kind === "cc") _navCase("cc", id); else sDid(id); }}
          />
        ),
      }
    : undefined;
  return (
    <>
      <EntityList
        {...caListCfg}
        bento={caBento}
        t={t}
        toast={toast}
        detailId={null}
        listSort={{}}
      />
      {/* 209 §19 — THE IMPORT'S FILE INPUT, BACK. EntityList keeps no <input> (it stays
          hookless, EntityList.tsx:109-112) and leaves it to the owning view; 57 §3c-ii
          (64c48743, 2026-08-10) moved this list into that shell and took the input out
          with the old toolbar, so « Import » clicked a ref nothing carried and the import
          could not be opened. Restored as it stood (287588a3). */}
      <input
        ref={caFileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.[0]) setCaImportFile(e.target.files[0]);
          e.target.value = "";
        }}
      />
      {caImportFile && (
        <CaseImportModal
          caImportFile={caImportFile}
          setCaImportFile={setCaImportFile}
          ca={ca}
          sCa={sCa}
          s={s}
          t={t}
          toast={toast}
          pools={listPools(cl, op, ocu, ex, cp, oc, me, ar, op2, co)} // 214 addendum 4 §25 — the window names a list entry
        />
      )}
      <ConfirmDeleteDialog dlg={confirmDlg} onCancel={cancelConfirm} />
      <UndoToast toast={undoToast} onUndo={runUndo} />
    </>
  );
};

export default CaView;
